import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import Modal from '../../components/Modal';
import CreateTaskModal from '../../components/workspace/task/CreateTaskModal';
import { SearchableSelect } from '../../components/workspace/filters/FilterDropdowns';
import { QueryBadge, AskAsQueryToggle, PendingQueriesChip, PendingQueriesList, QueryModal, QueriesPanel } from '../../components/workspace/review/ReviewQueries';
import '../../styles/BusinessReviews.css';

// ── tiny helpers ──────────────────────────────────────────────────────────────

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function periodFor(type, anchor = today()) {
  const d = new Date(anchor);
  if (type === 'daily') {
    return { start: anchor, end: anchor };
  }
  if (type === 'weekly') {
    const day = d.getDay(); // 0=Sun .. 6=Sat
    const sun = new Date(d); sun.setDate(d.getDate() - day);
    const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
    return { start: sun.toISOString().slice(0, 10), end: sat.toISOString().slice(0, 10) };
  }
  const [y, m] = anchor.split('-').map(Number);
  const start = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDay = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  return { start, end: lastDay };
}

const TYPE_LABEL = { daily: 'Daily Check-In', weekly: 'Weekly Review', monthly: 'Monthly Review' };

const TASK_STATUS_OPTS = [
  { value: 'on_track', label: 'On Track' },
  { value: 'needs_attention', label: 'Needs Attention' },
  { value: 'delayed', label: 'Delayed' },
];
const RATING_OPTS = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'satisfactory', label: 'Satisfactory' },
  { value: 'needs_improvement', label: 'Needs Improvement' },
];
const BADGE_META = {
  teamwork:        { emoji: '🤝', label: 'Teamwork' },
  innovation:      { emoji: '💡', label: 'Innovation' },
  leadership:      { emoji: '🌟', label: 'Leadership' },
  excellence:      { emoji: '🏆', label: 'Excellence' },
  mentorship:      { emoji: '🎓', label: 'Mentorship' },
  customer_focus:  { emoji: '💛', label: 'Customer Focus' },
  problem_solving: { emoji: '🔧', label: 'Problem Solving' },
};

const MONTHLY_RATING_OPTS = [
  { value: 'excellent',         label: 'Excellent' },
  { value: 'good',              label: 'Good' },
  { value: 'needs_improvement', label: 'Needs Improvement' },
  { value: 'critical',          label: 'Critical' },
];

// ── reusable UI pieces ────────────────────────────────────────────────────────

function Badge({ children, color = 'gray' }) {
  return <span className={`br-badge br-badge-${color}`}>{children}</span>;
}

function statusColor(s) {
  return { on_track: 'green', needs_attention: 'yellow', delayed: 'red', open: 'gray', closed: 'blue' }[s] || 'gray';
}

function taskPriorityColor(p) {
  return { urgent: 'red', high: 'yellow', medium: 'blue', low: 'gray' }[p] || 'gray';
}

function Section({ title, children }) {
  return (
    <div className="br-section">
      <div className="br-section-title">{title}</div>
      {children}
    </div>
  );
}

function StatCard({ label, value, colorClass = 'blue' }) {
  return (
    <div className="br-stat-card">
      <div className={`br-stat-value ${colorClass}`}>{value}</div>
      <div className="br-stat-label">{label}</div>
    </div>
  );
}

function FieldLabel({ children }) {
  return <label className="br-label">{children}</label>;
}

function TextArea({ label, value, onChange, rows = 3, disabled }) {
  return (
    <div className="br-field">
      {label && <FieldLabel>{label}</FieldLabel>}
      <textarea
        className="br-textarea"
        rows={rows}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, disabled, searchable }) {
  return (
    <div className="br-field">
      {label && <FieldLabel>{label}</FieldLabel>}
      {searchable ? (
        <SearchableSelect
          value={value || ''}
          onChange={onChange}
          options={[{ value: '', label: '— select —' }, ...options]}
          showAll={false}
          disabled={disabled}
          btnClass="form-select-btn"
          matchTriggerWidth
        />
      ) : (
        <select
          className="br-select"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">— select —</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    </div>
  );
}

function Input({ label, type = 'text', value, onChange, disabled }) {
  return (
    <div className="br-field">
      {label && <FieldLabel>{label}</FieldLabel>}
      <input
        className="br-input"
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

// ── Add Member control ───────────────────────────────────────────────────────
// Lets the reviewing manager pull in someone who isn't actually in their
// hierarchy (so never appears via the snapshot taken at creation) but who
// needs to be reviewed anyway — e.g. covering for another manager, or a
// cross-team contributor. Only usable while the review is still open/editable;
// closed reviews can't have their membership changed at all (PATCH already
// rejects that server-side).
function AddMemberButton({ appId, reviewId, currentUids, onAdded }) {
  const api = useApi();
  const [open, setOpen]         = useState(false);
  const [members, setMembers]   = useState([]);
  const [picked, setPicked]     = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    if (!open || !appId) return;
    api.listAppMembers(appId).then(r => setMembers(r.items || [])).catch(() => setMembers([]));
  }, [open, appId, api]);

  const currentSet = new Set((currentUids || []).map(String));
  const options = members
    .filter(m => !currentSet.has(String(m.user_id)))
    .map(m => ({
      value: String(m.user_id),
      label: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email,
    }));

  async function handleAdd() {
    if (!picked) return;
    setSaving(true);
    setError(null);
    try {
      const newAttendeeIds = [...new Set([...currentUids.map(Number), Number(picked)])];
      await api.updateBusinessReview(appId, reviewId, { attendee_ids: newAttendeeIds });
      setOpen(false);
      setPicked('');
      await onAdded?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button className="br-btn br-btn-ghost br-btn-sm" onClick={() => setOpen(true)}>
        + Add Member
      </button>
      <Modal open={open} title="Add a Member to This Review" onClose={() => setOpen(false)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 280 }}>
          <div style={{ fontSize: 12.5, color: 'var(--fg-mute)' }}>
            They don't need to be in your hierarchy — anyone in this workspace can be added.
          </div>
          <Select
            label="Member"
            value={picked}
            onChange={setPicked}
            options={options}
          />
          {error && <div style={{ color: 'var(--neg,#ef4444)', fontSize: 12.5 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="br-btn br-btn-ghost br-btn-sm" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            <button className="br-btn br-btn-primary br-btn-sm" onClick={handleAdd} disabled={saving || !picked}>
              {saving ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ── Create Review Modal ───────────────────────────────────────────────────────

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const result = new Date(Date.UTC(y, m - 1, d + n));
  return result.toISOString().slice(0, 10);
}

function snapToSunday(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const day = date.getDay(); // 0=Sun .. 6=Sat
  date.setDate(date.getDate() - day); // back to Sunday
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
}

function CreateReviewModal({ onClose, onCreate, initialType = 'daily', initialDate = null, appId }) {
  const api = useApi();
  const [type, setType] = useState(initialType === 'daily' ? 'daily' : 'other');
  const [subType, setSubType] = useState('weekly'); // 'weekly' | 'monthly' when type === 'other'
  const [reviewDate, setReviewDate] = useState(initialDate || today());
  const [weekStart, setWeekStart]   = useState(() => snapToSunday(initialDate || today()));
  const [monthSel, setMonthSel]     = useState(() => today().slice(0, 7));
  const [saving, setSaving]         = useState(false);

  // Scope state (for Other reviews)
  const [scopeType, setScopeType]   = useState('');
  const [scopeId, setScopeId]       = useState('');
  const [scopeOptions, setScopeOptions] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(false);

  useEffect(() => {
    if (type === 'other' && !scopeOptions && appId) {
      setScopeLoading(true);
      api.getBRScopeOptions(appId)
        .then(data => setScopeOptions(data))
        .catch(() => setScopeOptions({ departments: [], projects: [], contracts: [], managers: [] }))
        .finally(() => setScopeLoading(false));
    }
  }, [type, appId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset scope selection when scope type changes
  useEffect(() => { setScopeId(''); }, [scopeType]);

  // Compute actual period
  let period;
  if (type === 'daily') {
    period = { start: reviewDate, end: reviewDate };
  } else if (subType === 'weekly') {
    const endDate = addDays(weekStart, 6);
    period = { start: weekStart, end: endDate };
  } else {
    const [y, m] = monthSel.split('-').map(Number);
    const start = `${monthSel}-01`;
    const lastDay = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    period = { start, end: lastDay };
  }

  const actualType = type === 'daily' ? 'daily' : 'other';

  const scopeItemOptions = () => {
    if (!scopeOptions || !scopeType) return [];
    if (scopeType === 'department')       return (scopeOptions.departments || []).map(d => ({ value: String(d.id), label: d.name }));
    if (scopeType === 'project')          return (scopeOptions.projects    || []).map(p => ({ value: String(p.id), label: p.name }));
    if (scopeType === 'contract')         return (scopeOptions.contracts   || []).map(c => ({ value: String(c.id), label: c.name }));
    if (scopeType === 'entire_team')      return (scopeOptions.managers    || []).map(m => ({ value: String(m.id), label: m.name }));
    if (scopeType === 'direct_reportees') return [];
    return [];
  };

  const scopeItemLabel = () => {
    if (scopeType === 'department')       return 'Select Department';
    if (scopeType === 'project')          return 'Select Project';
    if (scopeType === 'contract')         return 'Select Agreement';
    if (scopeType === 'entire_team')      return 'Select Manager (reviews their full hierarchy)';
    return 'Select';
  };

  const canSubmit = type === 'daily'
    ? true
    : (type === 'other' ? (!!scopeType && (scopeType === 'direct_reportees' || !!scopeId)) : true);

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        type: actualType,
        review_date: period.start,
        period_start: period.start,
        period_end: period.end,
      };
      if (type === 'other' && subType !== 'daily') {
        payload.type = subType; // 'weekly' or 'monthly' — keep existing period logic but add scope
      }
      if (type === 'other') {
        payload.type = subType; // weekly or monthly
        payload.scope_type = scopeType;
        if (scopeType !== 'direct_reportees') payload.scope_id = Number(scopeId);
      }
      await onCreate(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="br-modal-overlay">
      <div className="br-modal">
        <div className="br-modal-title">New Business Review</div>

        <Select label="Review Type" value={type} onChange={setType} options={[
          { value: 'daily', label: 'Daily Check-In' },
          { value: 'other', label: 'Other Review (Weekly / Monthly)' },
        ]} />

        {type === 'daily' && (
          <Input label="Review Date" type="date" value={reviewDate} onChange={setReviewDate} />
        )}

        {type === 'other' && (
          <>
            <Select label="Period Type" value={subType} onChange={setSubType} options={[
              { value: 'weekly',  label: 'Weekly Review (Sun – Sat)' },
              { value: 'monthly', label: 'Monthly Review' },
            ]} />

            {subType === 'weekly' && (
              <div className="br-field">
                <FieldLabel>Pick any day in the week</FieldLabel>
                <input
                  className="br-input"
                  type="date"
                  value={weekStart}
                  onChange={e => setWeekStart(snapToSunday(e.target.value))}
                />
                <div style={{ fontSize: 11, color: 'var(--fg-mute,#94a3b8)', marginTop: 4 }}>
                  Auto-snaps to Sunday of selected week
                </div>
              </div>
            )}

            {subType === 'monthly' && (
              <div className="br-field">
                <FieldLabel>Select Month</FieldLabel>
                <input
                  className="br-input"
                  type="month"
                  value={monthSel}
                  onChange={e => setMonthSel(e.target.value)}
                />
              </div>
            )}

            {/* Scope picker */}
            <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-mute)', marginBottom: 8 }}>REVIEW SCOPE</div>
              {scopeLoading ? (
                <div style={{ fontSize: 13, color: 'var(--fg-mute)' }}>Loading…</div>
              ) : (
                <>
                  <Select label="Scope Type" value={scopeType} onChange={setScopeType} options={[
                    { value: 'department',       label: 'Department' },
                    { value: 'project',          label: 'Project' },
                    { value: 'contract',         label: 'Agreement' },
                    { value: 'direct_reportees', label: 'Direct Reportees' },
                    { value: 'entire_team',      label: 'Entire Team (by Manager)' },
                  ]} />

                  {scopeType === 'direct_reportees' && (
                    <div style={{ fontSize: 12, color: 'var(--fg-mute)', marginTop: 4 }}>
                      Will include your direct reportees only.
                    </div>
                  )}

                  {scopeType && scopeType !== 'direct_reportees' && (
                    <Select
                      label={scopeItemLabel()}
                      value={scopeId}
                      onChange={setScopeId}
                      options={scopeItemOptions()}
                      searchable
                    />
                  )}

                  {scopeType === 'entire_team' && scopeId && (
                    <div style={{ fontSize: 11, color: 'var(--fg-mute)', marginTop: 4 }}>
                      Will include the selected manager's full reporting hierarchy (all levels).
                    </div>
                  )}

                  {!scopeType && (
                    <div style={{ fontSize: 12, color: 'var(--fg-mute)', marginTop: 4 }}>
                      Choose who this review covers: a department, project, agreement, your direct reportees, or a manager's full hierarchy.
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        <div className="br-form-period-hint">
          Period: {fmt(period.start)} — {fmt(period.end)}
        </div>

        <div className="br-modal-actions">
          <button className="br-btn br-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="br-btn br-btn-primary" onClick={submit} disabled={saving || !canSubmit}>
            {saving ? 'Creating…' : 'Create Review'}
          </button>
        </div>
      </div>
    </div>
  );
}


function ReviewTasksPanel({ tasks, onTaskClick, title = 'Tasks in this Review', showLatestComment = false }) {
  if (!tasks || tasks.length === 0) return null;
  return (
    <div className="br-rc-item-list" style={{ margin: '5px 20px 12px' }}>
      <div className="br-mnew-panel-title" style={{ marginBottom: 6 }}>{title}</div>
      {tasks.map(t => {
        const lastComment = showLatestComment && t.comments?.length ? t.comments[t.comments.length - 1] : null;
        return (
          <div key={t.id} style={{ marginBottom: lastComment ? 6 : 0 }}>
            <div className="br-rc-item" style={{ cursor: 'pointer' }} onClick={() => onTaskClick && onTaskClick(t)}>
              <div className={`br-rc-item-dot br-rc-item-dot--${t.status === 'done' ? 'done' : 'active'}`} />
              <span className="br-rc-item-name">{t.title}</span>
              <span className="br-rc-item-meta">{t.due_on ? fmt(t.due_on) : '—'}</span>
              <span className={`br-rc-item-badge br-rc-item-badge--${t.status === 'done' ? 'done' : 'active'}`}>{TASK_STATUS_LABEL[t.status] || t.status}</span>
            </div>
            {lastComment && (
              <div style={{ margin: '2px 0 0 18px', fontSize: 12, color: 'var(--fg-mute)' }}>
                💬 <strong>{lastComment.author?.name || 'Unknown'}:</strong> {lastComment.body}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Review History Section (shown below Tasks Overview) ───────────────────────

function ReviewHistorySection({ appId, reviewId, review, userId, userName }) {
  const api = useApi();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [viewModal, setViewModal] = useState(null); // holds a history entry

  useEffect(() => {
    setLoading(true);
    api.getBRHistory(appId, reviewId, userId)
      .then(d => setData(d))
      .catch(() => setData({ history: [], current_review_tasks: [] }))
      .finally(() => setLoading(false));
  }, [appId, reviewId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scoped to the same review type as the one currently open — a Weekly
  // review's history should only show past Weekly reviews, not Daily or
  // Monthly ones mixed in. (Previously this filtered by exact period_start/
  // period_end instead, which could never match anything since two reviews
  // can never share the same manager+type+period — the create endpoint
  // already blocks that as a duplicate — so it silently emptied the list
  // for every weekly/monthly review.)
  const history = (data?.history || []).filter(h => h.type === review.type);

  const typeLabel = h => h.type === 'weekly' ? 'Weekly' : h.type === 'monthly' ? 'Monthly' : h.type === 'daily' ? 'Daily' : `Other${h.scope_type ? ` (${h.scope_type})` : ''}`;

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <div className="br-mnew-panel-title" style={{ marginBottom: 8 }}>Review History</div>
      {loading && <div style={{ fontSize: 13, color: 'var(--fg-mute)' }}>Loading…</div>}
      {!loading && history.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--fg-mute)' }}>No past reviews found for {userName}.</div>
      )}
      {/* Scrolls internally past a handful of rows instead of pushing the
          rest of the card (Manager Comments, Save button, etc.) further and
          further down as more history accumulates. */}
      <div style={{ maxHeight: 260, overflowY: 'auto', paddingRight: 4 }}>
      {history.map(h => (
        <div key={h.id} style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
          background: 'var(--bg-muted)', borderRadius: 7, marginBottom: 6, fontSize: 13,
        }}>
          <span style={{ fontWeight: 600, color: 'var(--brand)' }}>{typeLabel(h)}</span>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{fmt(h.period_start)} — {fmt(h.period_end)}</span>
          {h.manager && <span style={{ fontSize: 12, color: 'var(--fg-mute)' }}>by {h.manager.name}</span>}
          <span style={{
            marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 8,
            background: h.status === 'closed' ? '#22c55e20' : '#f59e0b20',
            color: h.status === 'closed' ? '#16a34a' : '#d97706', fontWeight: 600,
          }}>{h.status === 'closed' ? 'Completed' : 'Open'}</span>
          <button className="br-mnew-view-btn" onClick={() => setViewModal(h)}>View</button>
        </div>
      ))}
      </div>

      {/* Detail modal */}
      <Modal open={!!viewModal} title="Review Details" onClose={() => setViewModal(null)}>
        {viewModal && (
          <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>TYPE</span><br /><strong>{typeLabel(viewModal)}</strong></div>
              <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>PERIOD</span><br /><strong>{fmt(viewModal.period_start)} — {fmt(viewModal.period_end)}</strong></div>
              <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>REVIEWER</span><br /><strong>{viewModal.manager?.name || '—'}</strong></div>
              <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>REVIEWEE</span><br /><strong>{userName}</strong></div>
              <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>STATUS</span><br /><strong>{viewModal.status === 'closed' ? 'Completed' : 'Open'}</strong></div>
              {viewModal.score && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>SCORE</span><br /><strong>{viewModal.score}/5</strong></div>}
              {viewModal.overall_rating && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>OVERALL RATING</span><br /><strong>{viewModal.overall_rating}</strong></div>}
            </div>
            {viewModal.comment && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>COMMENT</span><br />{viewModal.comment}</div>}
            {viewModal.strengths && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>STRENGTHS</span><br />{viewModal.strengths}</div>}
            {viewModal.concerns && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>CONCERNS</span><br />{viewModal.concerns}</div>}
            {viewModal.development_areas && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>DEVELOPMENT AREAS</span><br />{viewModal.development_areas}</div>}
            {viewModal.recognition && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>RECOGNITION</span><br />{viewModal.recognition}</div>}
            {viewModal.task_notes && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>TASK NOTES</span><br />{viewModal.task_notes}</div>}
            {viewModal.project_notes && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>PROJECT NOTES</span><br />{viewModal.project_notes}</div>}
            {viewModal.goal_notes && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>GOAL NOTES</span><br />{viewModal.goal_notes}</div>}
            {viewModal.recognition_notes && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>RECOGNITION NOTES</span><br />{viewModal.recognition_notes}</div>}
            {viewModal.development_notes && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>DEVELOPMENT NOTES</span><br />{viewModal.development_notes}</div>}
            {(viewModal.review_tasks || []).length > 0 && (
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 6 }}>TASKS FROM THIS REVIEW</div>
                {viewModal.review_tasks.map(t => (
                  <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, fontSize: 13 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: t.status === 'done' ? '#22c55e' : '#f59e0b' }} />
                    <span style={{ flex: 1 }}>{t.title}</span>
                    {t.due_on && <span style={{ fontSize: 11, color: 'var(--fg-mute)' }}>{fmt(t.due_on)}</span>}
                    <span style={{ fontSize: 11, color: 'var(--fg-mute)' }}>{TASK_STATUS_LABEL[t.status] || t.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Dashboard Tab ─────────────────────────────────────────────────────────────

// ── Mini month calendar for daily check-ins ───────────────────────────────────
const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function DailyCalendar({ month, dailyReviews, onDayClick, onCreateForDate, canCreate }) {
  const [y, m] = month.split('-').map(Number);
  const firstDow = new Date(y, m - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(y, m, 0).getDate();
  const reviewByDate = {};
  for (const r of dailyReviews) {
    reviewByDate[r.review_date?.slice(0, 10)] = r;
  }

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const _td = new Date();
  const todayStr = `${_td.getFullYear()}-${String(_td.getMonth()+1).padStart(2,'0')}-${String(_td.getDate()).padStart(2,'0')}`;

  return (
    <div className="br-cal">
      <div className="br-cal-head">
        {DAY_NAMES.map(n => <div key={n} className="br-cal-dow">{n}</div>)}
      </div>
      <div className="br-cal-grid">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const dateKey = `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          const rev = reviewByDate[dateKey];
          const isToday = dateKey === todayStr;
          const colorCls = rev ? (rev.status === 'closed' ? ' br-cal-day-green' : ' br-cal-day-yellow') : '';
          const clickable = !rev && canCreate;
          return (
            <div
              key={dateKey}
              className={`br-cal-day${isToday ? ' br-cal-today' : ''}${rev ? ' br-cal-has-review' : ''}${colorCls}${clickable ? ' br-cal-day-empty-click' : ''}`}
              onClick={() => rev ? onDayClick(rev.id, rev.type) : (clickable ? onCreateForDate(dateKey) : undefined)}
              title={rev ? `${rev.status === 'closed' ? 'Completed' : 'Open'} check-in` : (clickable ? 'Click to create daily check-in' : '')}
            >
              <span className="br-cal-day-num">{d}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DashboardTab({ appId, onOpenReview, canCreate, monthFilter: monthFilterProp }) {
  const api = useApi();
  const [reporteeCount, setReporteeCount] = useState(null);
  const monthFilter = monthFilterProp || today().slice(0, 7);
  const [dailyReviews, setDailyReviews]         = useState([]);
  const [otherSummaryReviews, setOtherSummaryReviews] = useState([]);
  const [listReviews, setListReviews]           = useState([]);
  const [listType, setListType]                 = useState('all');
  const [loadingDaily, setLoadingDaily]         = useState(false);
  const [loadingList, setLoadingList]           = useState(false);
  const [showCreate, setShowCreate]             = useState(false);
  const [createType, setCreateType]             = useState('daily');
  const [createDate, setCreateDate]             = useState(null);
  const [noReporteesAlert, setNoReporteesAlert] = useState(false);
  const [duplicateAlert, setDuplicateAlert]     = useState(null); // { message, existing }
  const [deleteConfirm, setDeleteConfirm]       = useState(null); // { id, label }
  const [pendingReviews, setPendingReviews]     = useState([]);
  const [tasksCreated, setTasksCreated]         = useState(null);
  const [pendingManagerFilter, setPendingManagerFilter] = useState('');

  const getMonthRange = useCallback((month) => {
    const [y, m] = month.split('-').map(Number);
    const from = `${month}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const to = `${month}-${String(lastDay).padStart(2, '0')}`;
    return { from, to };
  }, []);

  const loadDaily = useCallback(() => {
    setLoadingDaily(true);
    const { from, to } = getMonthRange(monthFilter);
    api.listBusinessReviews(appId, { type: 'daily', from, to, per_page: 100, own_only: true })
      .then(d => setDailyReviews(d.reviews || []))
      .catch(console.error)
      .finally(() => setLoadingDaily(false));
  }, [api, appId, monthFilter, getMonthRange]);

  const loadOtherSummary = useCallback(() => {
    const { from, to } = getMonthRange(monthFilter);
    Promise.all([
      api.listBusinessReviews(appId, { type: 'weekly',  from, to, per_page: 100, own_only: true }),
      api.listBusinessReviews(appId, { type: 'monthly', from, to, per_page: 100, own_only: true }),
    ]).then(([w, mo]) =>
      setOtherSummaryReviews([...(w.reviews || []), ...(mo.reviews || [])])
    ).catch(console.error);
  }, [api, appId, monthFilter, getMonthRange]);

  const loadList = useCallback(() => {
    setLoadingList(true);
    const { from, to } = getMonthRange(monthFilter);
    // Load all types together — backend sorts open-first then by date
    Promise.all([
      api.listBusinessReviews(appId, { type: 'daily',   from, to, per_page: 100, own_only: true }),
      api.listBusinessReviews(appId, { type: 'weekly',  from, to, per_page: 100, own_only: true }),
      api.listBusinessReviews(appId, { type: 'monthly', from, to, per_page: 100, own_only: true }),
    ]).then(([d, w, mo]) => {
      const combined = [...(d.reviews || []), ...(w.reviews || []), ...(mo.reviews || [])];
      // Open first, then by date desc
      combined.sort((a, b) => {
        if (a.status === 'open' && b.status !== 'open') return -1;
        if (a.status !== 'open' && b.status === 'open') return 1;
        return (b.review_date || b.period_start || '').localeCompare(a.review_date || a.period_start || '');
      });
      setListReviews(combined);
    }).catch(console.error).finally(() => setLoadingList(false));
  }, [api, appId, monthFilter, getMonthRange]);

  const loadDashboardSummary = useCallback(() => {
    const { from, to } = getMonthRange(today().slice(0, 7));
    api.businessReviewDashboard(appId, { from, to })
      .then(d => { setReporteeCount(d.reporteeCount ?? 0); setPendingReviews(d.pending || []); })
      .catch(() => {});
  }, [api, appId, getMonthRange]);

  useEffect(() => { loadDashboardSummary(); }, [loadDashboardSummary]);

  useEffect(() => { loadDaily(); }, [loadDaily]);
  useEffect(() => { loadOtherSummary(); }, [loadOtherSummary]);
  useEffect(() => { loadList(); }, [loadList]);

  function handleCreateClick(type) {
    if (reporteeCount === 0) { setNoReporteesAlert(true); return; }
    setCreateType(type);
    setCreateDate(null);
    setShowCreate(true);
  }

  function handleCreateForDate(dateKey) {
    if (reporteeCount === 0) { setNoReporteesAlert(true); return; }
    setCreateType('daily');
    setCreateDate(dateKey);
    setShowCreate(true);
  }

  async function handleCreate(body) {
    try {
      await api.createBusinessReview(appId, body);
      setShowCreate(false);
      loadDaily();
      loadOtherSummary();
      loadList();
      loadDashboardSummary();
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'duplicate_review') {
        setShowCreate(false);
        setDuplicateAlert({ message: data.message, existing: data.existing });
      } else {
        throw err;
      }
    }
  }

  async function handleDelete(id) {
    try {
      await api.deleteBusinessReview(appId, id);
      setDeleteConfirm(null);
      loadDaily();
      loadOtherSummary();
      loadList();
      loadDashboardSummary();
    } catch (err) {
      setDeleteConfirm(null);
    }
  }

  const dailyClosed = dailyReviews.filter(r => r.status === 'closed').length;
  const dailyOpen   = dailyReviews.length - dailyClosed;
  const otherClosed = otherSummaryReviews.filter(r => r.status === 'closed').length;
  const otherOpen   = otherSummaryReviews.length - otherClosed;
  const thisMonthTotal = dailyReviews.length + otherSummaryReviews.length;
  const completionRate = thisMonthTotal > 0
    ? Math.round((dailyClosed + otherClosed) / thisMonthTotal * 100)
    : 0;

  const [y, m] = monthFilter.split('-').map(Number);
  const monthLabel = new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const pendingManagerOptions = useMemo(() => {
    const map = new Map();
    for (const r of pendingReviews) {
      if (r.manager_user_id && r.manager?.name) map.set(r.manager_user_id, r.manager.name);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [pendingReviews]);

  const visiblePendingReviews = pendingManagerFilter
    ? pendingReviews.filter(r => String(r.manager_user_id) === String(pendingManagerFilter))
    : pendingReviews;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Modal open={noReporteesAlert} title="No Team Members Found" onClose={() => setNoReporteesAlert(false)} size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 28, lineHeight: 1 }}>&#9888;&#65039;</span>
            <div style={{ fontSize: 14, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
              You don't have any reportees assigned. Please contact your administrator.
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-secondary" style={{ padding: '7px 22px' }} onClick={() => setNoReporteesAlert(false)}>Close</button>
          </div>
        </div>
      </Modal>

      {/* Duplicate review alert */}
      <Modal open={!!duplicateAlert} title="Review Already Exists" onClose={() => setDuplicateAlert(null)} size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 28, lineHeight: 1 }}>⚠️</span>
            <div style={{ fontSize: 14, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
              {duplicateAlert?.message}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {duplicateAlert?.existing && (
              <button
                className="br-btn br-btn-danger br-btn-sm"
                onClick={() => { setDeleteConfirm({ id: duplicateAlert.existing.id, label: `${duplicateAlert.existing.period_start} – ${duplicateAlert.existing.period_end}` }); setDuplicateAlert(null); }}
              >
                Delete Existing Review
              </button>
            )}
            <button className="btn-secondary" style={{ padding: '7px 22px' }} onClick={() => setDuplicateAlert(null)}>Close</button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!deleteConfirm} title="Delete Review?" onClose={() => setDeleteConfirm(null)} size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 14, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
            Are you sure you want to permanently delete the review for <strong>{deleteConfirm?.label}</strong>? This cannot be undone.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-secondary" style={{ padding: '7px 22px' }} onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="br-btn br-btn-danger br-btn-sm" onClick={() => handleDelete(deleteConfirm?.id)}>Delete</button>
          </div>
        </div>
      </Modal>

      {/* Top: 4 stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>

        {/* Daily Check-Ins */}
        <div className="br-info-card">
          <div className="br-info-card-icon" style={{ background: 'rgba(20,184,166,0.12)', color: '#14b8a6' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
              <rect x="9" y="3" width="6" height="4" rx="1"/>
              <path d="M9 12h6M9 16h4"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div className="br-info-card-label">Daily Check-Ins</div>
            <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
              <div>
                <div className="br-info-big" style={{ color: '#22c55e' }}>{dailyClosed}</div>
                <div className="br-info-sub">Closed</div>
              </div>
              <div>
                <div className="br-info-big" style={{ color: '#f59e0b' }}>{dailyOpen}</div>
                <div className="br-info-sub">Open</div>
              </div>
            </div>
          </div>
        </div>

        {/* Other Reviews */}
        <div className="br-info-card">
          <div className="br-info-card-icon" style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div className="br-info-card-label">Other Reviews</div>
            <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
              <div>
                <div className="br-info-big" style={{ color: '#22c55e' }}>{otherClosed}</div>
                <div className="br-info-sub">Closed</div>
              </div>
              <div>
                <div className="br-info-big" style={{ color: '#f59e0b' }}>{otherOpen}</div>
                <div className="br-info-sub">Open</div>
              </div>
            </div>
            {tasksCreated > 0 && <div className="br-info-sub" style={{ marginTop: 4 }}>{tasksCreated} task{tasksCreated !== 1 ? 's' : ''} created from reviews</div>}
          </div>
        </div>

        {/* This Month */}
        <div className="br-info-card">
          <div className="br-info-card-icon" style={{ background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div className="br-info-card-label">This Month</div>
            <div className="br-info-big" style={{ color: '#8b5cf6', marginTop: 4 }}>{thisMonthTotal}</div>
            <div className="br-info-sub">Reviews Scheduled</div>
          </div>
        </div>

        {/* Completion Rate */}
        <div className="br-info-card">
          <div className="br-info-card-icon" style={{ background: 'rgba(249,115,22,0.12)', color: '#f97316' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div className="br-info-card-label">Completion Rate</div>
            <div className="br-info-big" style={{ color: '#f97316', marginTop: 4 }}>{completionRate}%</div>
            <div className="br-info-sub">Across All Reviews</div>
          </div>
        </div>
      </div>

      {/* Top: Calendar (left) + Upcoming Reviews (right) */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>

        {/* Left: Queries | Calendar — opens on Queries only when some are pending */}
        <QueriesPanel
          appId={appId}
          scopes={['asked']}
          month={monthFilter}
          onLoaded={d => setTasksCreated(d.asked?.tasks_created ?? null)}
          className="br-dash-panel"
          style={{ flex: '0 0 38%', minWidth: 0 }}
          headClassName="br-dash-panel-header"
          titleClassName="br-dash-panel-title"
          title="Daily Check-In Calendar"
          calendarLegend={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="br-cal-legend">
                <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:'#f59e0b', marginRight:4 }} />
                Open
              </span>
              <span className="br-cal-legend">
                <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:'#22c55e', marginRight:4 }} />
                Completed
              </span>
            </div>
          }
          calendar={<>
            <div style={{ padding:'6px 16px 2px', fontWeight:600, fontSize:13, color:'var(--text-secondary)', textAlign:'center' }}>
              {monthLabel}
            </div>
            {loadingDaily ? (
              <div className="br-loading" style={{ padding: '16px 0' }}>Loading...</div>
            ) : (
              <DailyCalendar month={monthFilter} dailyReviews={dailyReviews} onDayClick={onOpenReview} onCreateForDate={handleCreateForDate} canCreate={canCreate} />
            )}
          </>}
        />

        {/* Right: Reviews */}
        <div className="br-dash-panel" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="br-dash-panel-header" style={{ paddingBottom: 8 }}>
            <span className="br-dash-panel-title">Reviews</span>
            {canCreate && (
              <button className="br-btn br-btn-primary br-btn-sm" onClick={() => handleCreateClick('daily')}>+ Create Review</button>
            )}
          </div>
          {loadingList ? (
            <div className="br-loading" style={{ padding: '16px 0' }}>Loading...</div>
          ) : listReviews.length === 0 ? (
            <div style={{ padding: '16px', fontSize: 13, color: 'var(--fg-mute,#94a3b8)', textAlign: 'center' }}>
              No reviews for this period.
            </div>
          ) : (
            <div className="br-dash-review-list" style={{ maxHeight: 224, overflowY: 'auto', minHeight: 0 }}>
              {listReviews.map(r => {
                const dateStr = r.review_date || r.period_start || '';
                const d = dateStr ? new Date(dateStr) : null;
                const dayNum = d ? d.getDate() : '';
                const mon = d ? d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase() : '';
                const yr = d ? d.getFullYear() : '';
                const typeLabel = r.type === 'daily' ? 'Daily Check-In' : r.type === 'weekly' ? 'Weekly Review' : 'Monthly Review';
                return (
                  <div key={r.id} className="br-uprev-row" onClick={() => onOpenReview(r.id, r.type)}>
                    <div className="br-uprev-date-block">
                      <span className="br-uprev-day">{dayNum}</span>
                      <span className="br-uprev-mon">{mon}</span>
                      <span className="br-uprev-yr">{yr}</span>
                    </div>
                    <div className="br-uprev-info">
                      {r.period_start && r.period_end ? (
                        <div className="br-uprev-range">{fmt(r.period_start)} &ndash; {fmt(r.period_end)}</div>
                      ) : (
                        <div className="br-uprev-range">{fmt(dateStr)}</div>
                      )}
                      <div className="br-uprev-meta">
                        {r.participant_count ? (
                          <span className="br-uprev-participants">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                            {r.participant_count} Participants
                          </span>
                        ) : null}
                        <span className="br-uprev-type">{typeLabel}</span>
                      </div>
                    </div>
                    <span className={`br-badge ${r.status === 'closed' ? 'br-badge-green' : 'br-badge-yellow'}`}>
                      {r.status === 'closed' ? 'Completed' : 'Open'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Pending Reviews (no date filter) */}
      <div className="br-dash-panel">
        <div className="br-dash-panel-header" style={{ position: 'relative' }}>
          <span className="br-dash-panel-title">Pending Reviews</span>
          {pendingManagerOptions.length > 1 && (
            <select
              className="br-select"
              value={pendingManagerFilter}
              onChange={e => setPendingManagerFilter(e.target.value)}
              style={{
                position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
                width: 140, fontSize: 12.5,
              }}
            >
              <option value="">All Managers</option>
              {pendingManagerOptions.map(mgr => (
                <option key={mgr.id} value={mgr.id}>{mgr.name}</option>
              ))}
            </select>
          )}
          <span className="br-cal-legend">{visiblePendingReviews.length} open</span>
        </div>
        {visiblePendingReviews.length === 0 ? (
          <div style={{ padding: '16px', fontSize: 13, color: 'var(--fg-mute,#94a3b8)', textAlign: 'center' }}>
            No pending reviews.
          </div>
        ) : (
          <div className="br-dash-review-list" style={{ maxHeight: 336, overflowY: 'auto' }}>
            {visiblePendingReviews.map(r => {
              const dateStr = r.review_date || r.period_start || '';
              const typeLabel = r.type === 'daily' ? 'Daily Check-In' : r.type === 'weekly' ? 'Weekly Review' : 'Monthly Review';
              return (
                <div key={r.id} className="br-uprev-row" onClick={() => onOpenReview(r.id, r.type)}>
                  <div className="br-uprev-range" style={{ flex: '0 0 auto' }}>
                    {r.period_start && r.period_end ? `${fmt(r.period_start)} – ${fmt(r.period_end)}` : fmt(dateStr)}
                  </div>
                  <div className="br-uprev-meta" style={{ flex: 1, justifyContent: 'center' }}>
                    {r.manager?.name && <span className="br-uprev-participants">Manager: {r.manager.name}</span>}
                    <span className="br-uprev-type">{typeLabel}</span>
                  </div>
                  <span className="br-badge br-badge-yellow">{r.days_pending} day{r.days_pending === 1 ? '' : 's'} pending</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateReviewModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
          initialType={createType}
          initialDate={createDate}
          appId={appId}
        />
      )}
    </div>
  );
}

// ── Review List Tab ───────────────────────────────────────────────────────────

function ReviewListTab({ appId, type, canCreate, onOpenReview }) {
  // onOpenReview(id, type)
  const api = useApi();
  const { searchQuery } = useWorkspace();
  const [reviews, setReviews]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showCreate, setShowCreate]     = useState(false);
  const [noReporteesAlert, setNoReporteesAlert] = useState(false);
  const [duplicateAlert, setDuplicateAlert] = useState(null);
  const [deleteConfirm, setDeleteConfirm]   = useState(null);
  const [reporteeCount, setReporteeCount] = useState(null);
  const [monthFilter, setMonthFilter]   = useState(() => today().slice(0, 7)); // "YYYY-MM"

  const load = useCallback(() => {
    setLoading(true);
    const params = { type, per_page: 50, own_only: true };
    if (type === 'monthly' && monthFilter) {
      const [y, m] = monthFilter.split('-').map(Number);
      params.from = `${monthFilter}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      params.to = `${monthFilter}-${String(lastDay).padStart(2, '0')}`;
    }
    api.listBusinessReviews(appId, params)
      .then(d => setReviews(d.reviews || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [appId, type, monthFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const now = today().slice(0, 7);
    const [y, m] = now.split('-').map(Number);
    const from = `${now}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const to = `${now}-${String(lastDay).padStart(2, '0')}`;
    api.businessReviewDashboard(appId, { from, to })
      .then(d => setReporteeCount(d.reporteeCount ?? null))
      .catch(() => {});
  }, [api, appId]);

  function handleCreateClick() {
    if (reporteeCount !== null && reporteeCount === 0) {
      setNoReporteesAlert(true);
      return;
    }
    setShowCreate(true);
  }

  async function handleCreate(body) {
    try {
      await api.createBusinessReview(appId, body);
      setShowCreate(false);
      load();
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'duplicate_review') {
        setShowCreate(false);
        setDuplicateAlert({ message: data.message, existing: data.existing });
      } else {
        throw err;
      }
    }
  }

  async function handleDelete(id) {
    try {
      await api.deleteBusinessReview(appId, id);
      setDeleteConfirm(null);
      load();
    } catch { setDeleteConfirm(null); }
  }

  return (
    <div>
      <Modal open={noReporteesAlert} title="No Team Members Found" onClose={() => setNoReporteesAlert(false)} size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 28, lineHeight: 1 }}>⚠️</span>
            <div style={{ fontSize: 14, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
              You don't have any reportees assigned to you. A business review requires at least one team member reporting to you.
              <br /><br />
              Please contact your administrator to assign team members to you before creating a review.
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-secondary" style={{ padding: '7px 22px' }} onClick={() => setNoReporteesAlert(false)}>
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Duplicate review alert */}
      <Modal open={!!duplicateAlert} title="Review Already Exists" onClose={() => setDuplicateAlert(null)} size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 28, lineHeight: 1 }}>⚠️</span>
            <div style={{ fontSize: 14, color: 'var(--fg-mute)', lineHeight: 1.6 }}>{duplicateAlert?.message}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {duplicateAlert?.existing && (
              <button
                className="br-btn br-btn-danger br-btn-sm"
                onClick={() => { setDeleteConfirm({ id: duplicateAlert.existing.id, label: `${duplicateAlert.existing.period_start} – ${duplicateAlert.existing.period_end}` }); setDuplicateAlert(null); }}
              >Delete Existing Review</button>
            )}
            <button className="btn-secondary" style={{ padding: '7px 22px' }} onClick={() => setDuplicateAlert(null)}>Close</button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!deleteConfirm} title="Delete Review?" onClose={() => setDeleteConfirm(null)} size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 14, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
            Are you sure you want to permanently delete the review for <strong>{deleteConfirm?.label}</strong>? This cannot be undone.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-secondary" style={{ padding: '7px 22px' }} onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="br-btn br-btn-danger br-btn-sm" onClick={() => handleDelete(deleteConfirm?.id)}>Delete</button>
          </div>
        </div>
      </Modal>

      <div className="br-list-header">
        <div className="br-list-title">{TYPE_LABEL[type]}s</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {type === 'monthly' && (
            <input
              type="month"
              className="br-input"
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              style={{ fontSize: 13 }}
            />
          )}
          {canCreate && (
            <button className="br-btn br-btn-primary" onClick={handleCreateClick}>
              + New {TYPE_LABEL[type]}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="br-loading">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="br-empty">No {type} reviews yet.</div>
      ) : (
        <div className="br-card br-table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
          <table className="br-table">
            <thead>
              <tr>
                {['#', 'Date', 'Period', 'Manager', 'Status', 'Duration', ''].map(h => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {reviews.filter(r => { const sq = (searchQuery || '').toLowerCase().trim(); return !sq || [r.review_date, r.period_start, r.period_end, r.manager?.name, r.status].some(f => (f || '').toLowerCase().includes(sq)); }).map((r, idx) => (
                <tr key={r.id}>
                  <td className="br-td-muted" style={{ width: 36, textAlign: 'center' }}>{idx + 1}</td>
                  <td className="br-td-bold">{fmt(r.review_date)}</td>
                  <td className="br-td-muted">{fmt(r.period_start)} — {fmt(r.period_end)}</td>
                  <td>{r.manager?.name || '—'}</td>
                  <td><Badge color={statusColor(r.status)}>{r.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</Badge></td>
                  <td className="br-td-muted">{r.duration_minutes ? `${r.duration_minutes} min` : '—'}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="br-btn br-btn-ghost br-btn-sm" onClick={() => onOpenReview(r.id, r.type)}>Open</button>
                    {canCreate && (
                      <button
                        className="br-btn br-btn-ghost br-btn-sm"
                        style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                        onClick={() => setDeleteConfirm({ id: r.id, label: `${fmt(r.period_start)} – ${fmt(r.period_end)}` })}
                      >Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateReviewModal onClose={() => setShowCreate(false)} onCreate={handleCreate} initialType={type} appId={appId} />
      )}
    </div>
  );
}

// ── Assessment Panel ──────────────────────────────────────────────────────────

function AssessmentPanel({ reviewId, reporteeData, reviewType, closed, appId, onRefresh }) {
  const api = useApi();
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const setDraft = (uid, field, value) => {
    setDrafts(prev => ({ ...prev, [uid]: { ...(prev[uid] || {}), [field]: value } }));
  };

  const getDraft = (uid) => {
    const base = reporteeData[uid]?.assessment || {};
    return { ...base, ...(drafts[uid] || {}) };
  };

  async function save(uid) {
    setSaving(s => ({ ...s, [uid]: true }));
    try {
      await api.upsertBRAssessment(appId, reviewId, uid, getDraft(uid));
      setDrafts(prev => { const n = { ...prev }; delete n[uid]; return n; });
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(s => ({ ...s, [uid]: false }));
    }
  }

  const uids = Object.keys(reporteeData);

  if (!uids.length) return <div className="br-no-reportees">No reportees found for this review.</div>;

  return (
    <div>
      {uids.map(uid => {
        const rd = reporteeData[uid];
        const d = getDraft(uid);
        const isDirty = !!drafts[uid] && Object.keys(drafts[uid]).length > 0;

        return (
          <div key={uid} className="br-assessment-card">
            <div className="br-assessment-header">
              <div className="br-assessment-name">{rd.user?.name || `User ${uid}`}</div>
              <div className="br-assessment-stats">
                <span className="br-assessment-stat">Done: <b>{rd.done_count}</b></span>
                <span className="br-assessment-stat">
                  Overdue: <b style={{ color: rd.overdue_count > 0 ? 'var(--neg, #ef4444)' : 'var(--pos, #22c55e)' }}>{rd.overdue_count}</b>
                </span>
                {!closed && isDirty && (
                  <button className="br-btn br-btn-primary br-btn-sm" onClick={() => save(uid)} disabled={saving[uid]}>
                    {saving[uid] ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>
            </div>

            {rd.tasks?.length > 0 && (
              <div className="br-task-list">
                <div className="br-task-list-title">Tasks in Period</div>
                {rd.tasks.slice(0, 8).map(t => (
                  <div key={t.id} className="br-task-row">
                    <Badge color={taskPriorityColor(t.priority)}>{t.priority}</Badge>
                    <span className="br-task-title">{t.title}</span>
                    {t.area_name && <span className="br-task-area">{t.area_name}</span>}
                    <span className="br-task-date">{fmt(t.due_on)}</span>
                  </div>
                ))}
                {rd.tasks.length > 8 && <div className="br-task-overflow">+{rd.tasks.length - 8} more</div>}
              </div>
            )}

            <div className="br-form-2col">
              <Select label="Task Status" value={d.task_status} onChange={v => setDraft(uid, 'task_status', v)}
                options={TASK_STATUS_OPTS} disabled={closed} />
              {reviewType !== 'daily' && (
                <Select label="Rating" value={d.rating} onChange={v => setDraft(uid, 'rating', v)}
                  options={RATING_OPTS} disabled={closed} />
              )}
            </div>

            <TextArea label="Comment" value={d.comment} onChange={v => setDraft(uid, 'comment', v)} disabled={closed} />

            {reviewType === 'monthly' && (
              <div className="br-form-2col">
                <TextArea label="Strengths" value={d.strengths} onChange={v => setDraft(uid, 'strengths', v)} disabled={closed} rows={2} />
                <TextArea label="Concerns" value={d.concerns} onChange={v => setDraft(uid, 'concerns', v)} disabled={closed} rows={2} />
                <TextArea label="Development Areas" value={d.development_areas} onChange={v => setDraft(uid, 'development_areas', v)} disabled={closed} rows={2} />
                <TextArea label="Recognition" value={d.recognition} onChange={v => setDraft(uid, 'recognition', v)} disabled={closed} rows={2} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Action Items Panel ────────────────────────────────────────────────────────

function ActionItemsPanel({ reviewId, actionItems, closed, appId, reportees, onRefresh }) {
  const api = useApi();
  const [form, setForm] = useState({ description: '', due_date: '', assigned_to_user_id: '' });
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  async function addItem() {
    if (!form.description.trim()) return;
    setSaving(true);
    try {
      await api.addBRActionItem(appId, reviewId, {
        description: form.description,
        due_date: form.due_date || undefined,
        assigned_to_user_id: form.assigned_to_user_id ? parseInt(form.assigned_to_user_id) : undefined,
      });
      setForm({ description: '', due_date: '', assigned_to_user_id: '' });
      setAdding(false);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(ai, status) {
    await api.updateBRActionItem(appId, reviewId, ai.id, { status });
    onRefresh();
  }

  async function remove(ai) {
    if (!window.confirm('Remove this action item?')) return;
    await api.deleteBRActionItem(appId, reviewId, ai.id);
    onRefresh();
  }

  const statusColors = { open: 'gray', in_progress: 'yellow', done: 'green' };

  return (
    <div>
      {actionItems.length === 0 && !adding && (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>No action items yet.</div>
      )}
      {actionItems.map(ai => (
        <div key={ai.id} className="br-action-item">
          <div className="br-action-item-body">
            <div className={`br-action-item-text${ai.status === 'done' ? ' done' : ''}`}>{ai.description}</div>
            <div className="br-action-item-meta">
              {ai.user?.name && <span>→ {ai.user.name}</span>}
              {ai.due_date && <span style={{ marginLeft: 8 }}>{fmt(ai.due_date)}</span>}
            </div>
          </div>
          <Badge color={statusColors[ai.status] || 'gray'}>{ai.status}</Badge>
          {!closed && (
            <div className="br-action-item-controls">
              {ai.status !== 'done' && (
                <button className="br-btn br-btn-ghost br-btn-sm"
                  onClick={() => updateStatus(ai, ai.status === 'open' ? 'in_progress' : 'done')}>
                  {ai.status === 'open' ? 'Start' : 'Done'}
                </button>
              )}
              <button className="br-btn br-btn-danger br-btn-sm" onClick={() => remove(ai)}>✕</button>
            </div>
          )}
        </div>
      ))}

      {!closed && (
        adding ? (
          <div className="br-action-form">
            <Input label="Description" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} />
            <div className="br-form-2col">
              <Input label="Due Date" type="date" value={form.due_date} onChange={v => setForm(f => ({ ...f, due_date: v }))} />
              <Select label="Assign To" value={String(form.assigned_to_user_id || '')}
                onChange={v => setForm(f => ({ ...f, assigned_to_user_id: v }))}
                options={reportees.map(u => ({ value: String(u.id), label: u.name }))}
                searchable />
            </div>
            <div className="br-modal-actions">
              <button className="br-btn br-btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
              <button className="br-btn br-btn-primary" onClick={addItem} disabled={saving || !form.description.trim()}>
                {saving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        ) : (
          <button className="br-add-action-btn" onClick={() => setAdding(true)}>+ Add Action Item</button>
        )
      )}
    </div>
  );
}

// ── Close Review Modal ────────────────────────────────────────────────────────

function CloseReviewModal({ review, onClose, onClosed, appId }) {
  const api = useApi();
  const [form, setForm] = useState({
    summary: review.summary || '',
    achievements: review.achievements || '',
    key_risks: review.key_risks || '',
    improvement_plans: review.improvement_plans || '',
    next_period_focus: review.next_period_focus || '',
  });
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      await api.closeBusinessReview(appId, review.id, form);
      onClosed();
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="br-modal-overlay">
      <div className="br-modal br-modal-lg">
        <div className="br-modal-title">Close Review</div>
        <TextArea label="Summary" value={form.summary} onChange={v => setForm(f => ({ ...f, summary: v }))} rows={3} />
        {review.type !== 'daily' && (
          <TextArea label="Key Risks" value={form.key_risks} onChange={v => setForm(f => ({ ...f, key_risks: v }))} rows={2} />
        )}
        {review.type === 'monthly' && (
          <>
            <TextArea label="Achievements" value={form.achievements} onChange={v => setForm(f => ({ ...f, achievements: v }))} rows={2} />
            <TextArea label="Improvement Plans" value={form.improvement_plans} onChange={v => setForm(f => ({ ...f, improvement_plans: v }))} rows={2} />
            <TextArea label="Next Period Focus" value={form.next_period_focus} onChange={v => setForm(f => ({ ...f, next_period_focus: v }))} rows={2} />
          </>
        )}
        <div className="br-modal-actions">
          <button className="br-btn br-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="br-btn br-btn-success" onClick={submit} disabled={saving}>
            {saving ? 'Closing…' : 'Close Review'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Review Workspace ──────────────────────────────────────────────────────────

function ReviewWorkspace({ reviewId, appId, onBack }) {
  const api = useApi();
  const { myRole } = useWorkspace();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('assessments');
  const [showClose, setShowClose] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.getBusinessReview(appId, reviewId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [appId, reviewId]);

  useEffect(() => { load(); }, [load]);

  function exportPDF() {
    if (!data) return;
    const { review, manager, reportees, assessments, actionItems, reporteeData, projects, routineCompletion } = data;
    const typeLabel = TYPE_LABEL[review.type];

    const reporteeRows = reportees.map(u => {
      const rd = reporteeData[u.id];
      const asmnt = assessments.find(a => a.platform_user_id === u.id);
      return `
        <div style="margin-bottom:20px;padding:14px;border:1px solid #e5e7eb;border-radius:8px;">
          <b style="font-size:15px">${u.name}</b>
          <div style="margin-top:8px;display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:8px;">
            <div><small>Done</small><br><b>${rd?.done_count || 0}</b></div>
            <div><small>Overdue</small><br><b style="color:#ef4444">${rd?.overdue_count || 0}</b></div>
            <div><small>Task Status</small><br><b>${asmnt?.task_status?.replace('_', ' ') || '—'}</b></div>
            ${review.type !== 'daily' ? `<div><small>Rating</small><br><b>${asmnt?.rating?.replace('_', ' ') || '—'}</b></div>` : ''}
          </div>
          ${asmnt?.comment ? `<div style="margin-top:8px"><small>Comment:</small> ${asmnt.comment}</div>` : ''}
          ${review.type === 'monthly' && asmnt?.strengths ? `<div style="margin-top:4px"><small>Strengths:</small> ${asmnt.strengths}</div>` : ''}
          ${review.type === 'monthly' && asmnt?.concerns ? `<div style="margin-top:4px"><small>Concerns:</small> ${asmnt.concerns}</div>` : ''}
        </div>
      `;
    }).join('');

    const actionRows = actionItems.length ? actionItems.map(ai =>
      `<tr><td style="padding:6px">${ai.description}</td><td style="padding:6px">${ai.user?.name || '—'}</td><td style="padding:6px">${fmt(ai.due_date)}</td><td style="padding:6px">${ai.status}</td></tr>`
    ).join('') : '<tr><td colspan="4" style="padding:6px;color:#9ca3af">None</td></tr>';

    const html = `
      <!DOCTYPE html><html><head><title>${typeLabel} — ${fmt(review.review_date)}</title>
      <style>body{font-family:sans-serif;color:#111827;padding:32px;max-width:800px;margin:0 auto}h1{font-size:22px;margin-bottom:4px}h2{font-size:15px;color:#374151;margin:20px 0 8px}table{width:100%;border-collapse:collapse;font-size:13px}th{text-align:left;padding:8px;background:#f3f4f6;font-weight:600}td{padding:6px;border-bottom:1px solid #f3f4f6}small{color:#6b7280;font-size:11px}@media print{body{padding:16px}}</style>
      </head><body>
        <h1>${typeLabel}</h1>
        <div style="color:#6b7280;font-size:13px;margin-bottom:16px">
          Date: ${fmt(review.review_date)} &nbsp;|&nbsp; Period: ${fmt(review.period_start)} — ${fmt(review.period_end)} &nbsp;|&nbsp; Manager: ${manager?.name || '—'} &nbsp;|&nbsp; Duration: ${review.duration_minutes ? review.duration_minutes + ' min' : '—'}
        </div>
        ${review.meeting_notes ? `<div style="margin-bottom:16px;padding:12px;background:#f8fafc;border-radius:6px"><b>Meeting Notes</b><p style="margin-top:6px;white-space:pre-wrap">${review.meeting_notes}</p></div>` : ''}
        <h2>Team Assessments</h2>${reporteeRows}
        ${projects.length ? `<h2>Projects</h2><table><thead><tr><th>Project</th><th>Status</th><th>Due</th><th>Done Tasks</th><th>Pending</th></tr></thead><tbody>${projects.map(p => `<tr><td>${p.title}</td><td>${p.status}</td><td>${fmt(p.due_date)}</td><td>${p.done_tasks}</td><td>${p.pending_tasks}</td></tr>`).join('')}</tbody></table>` : ''}
        ${routineCompletion.length ? `<h2>Routine Completion</h2><table><thead><tr><th>Routine</th><th>Area</th><th>Done</th><th>Total</th></tr></thead><tbody>${routineCompletion.map(r => `<tr><td>${r.description}</td><td>${r.area_name || '—'}</td><td>${r.done}</td><td>${r.total}</td></tr>`).join('')}</tbody></table>` : ''}
        <h2>Action Items</h2><table><thead><tr><th>Item</th><th>Assigned To</th><th>Due Date</th><th>Status</th></tr></thead><tbody>${actionRows}</tbody></table>
        ${review.summary ? `<h2>Summary</h2><p style="white-space:pre-wrap">${review.summary}</p>` : ''}
        ${review.type === 'monthly' && review.achievements ? `<h2>Achievements</h2><p style="white-space:pre-wrap">${review.achievements}</p>` : ''}
        ${review.key_risks ? `<h2>Key Risks</h2><p style="white-space:pre-wrap">${review.key_risks}</p>` : ''}
        ${review.type === 'monthly' && review.improvement_plans ? `<h2>Improvement Plans</h2><p style="white-space:pre-wrap">${review.improvement_plans}</p>` : ''}
        ${review.type === 'monthly' && review.next_period_focus ? `<h2>Next Period Focus</h2><p style="white-space:pre-wrap">${review.next_period_focus}</p>` : ''}
      </body></html>
    `;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.print();
  }

  if (loading) return <div className="br-loading">Loading…</div>;
  if (!data) return <div className="br-loading" style={{ color: 'var(--neg, #ef4444)' }}>Failed to load review.</div>;

  const { review, manager, reportees, assessments, actionItems, reporteeData, projects, routineCompletion } = data;
  const closed = review.status === 'closed';
  const sections = ['assessments', 'projects', 'routines', 'action-items', 'notes'];

  return (
    <div>
      <div className="br-workspace-header">
        <button className="btn-back" onClick={onBack}>←</button>
        <div className="br-workspace-meta">
          <div className="br-workspace-title-row">
            <span className="br-workspace-title">{TYPE_LABEL[review.type]}</span>
            <Badge color={statusColor(review.status)}>{review.status}</Badge>
          </div>
          <div className="br-workspace-subtitle">
            {fmt(review.review_date)} &nbsp;·&nbsp; {fmt(review.period_start)} — {fmt(review.period_end)} &nbsp;·&nbsp; {manager?.name}
          </div>
        </div>
        <div className="br-workspace-actions">
          <button className="br-btn br-btn-ghost" onClick={exportPDF}>Export PDF</button>
          {!closed && (
            <button className="br-btn br-btn-success" onClick={() => setShowClose(true)}>Close Review</button>
          )}
        </div>
      </div>

      {/* Section sub-tabs */}
      <div className="rh-tabs">
        {sections.map(s => (
          <button key={s}
            className={`rh-tab${activeSection === s ? ' active' : ''}`}
            onClick={() => setActiveSection(s)}>
            {s === 'action-items' ? 'Action Items' : s.charAt(0).toUpperCase() + s.slice(1)}
            {s === 'action-items' && actionItems.length > 0 && (
              <span className="br-count-chip">{actionItems.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="rh-tab-content">
        {activeSection === 'assessments' && (
          <AssessmentPanel reviewId={review.id} reporteeData={reporteeData} reviewType={review.type} closed={closed} appId={appId} onRefresh={load} />
        )}

        {activeSection === 'projects' && (
          <div className="br-card br-table-wrap">
            {projects.length === 0 ? (
              <div className="br-no-reportees">No projects in this period.</div>
            ) : (
              <table className="br-table">
                <thead>
                  <tr>{['Project', 'Status', 'Due Date', 'Done Tasks', 'Pending'].map(h => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {projects.map(p => (
                    <tr key={p.id}>
                      <td className="br-td-bold">{p.title}</td>
                      <td><Badge color={p.status === 'active' ? 'blue' : p.status === 'delayed' ? 'red' : 'gray'}>{p.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</Badge></td>
                      <td className="br-td-muted">{fmt(p.due_date)}</td>
                      <td className="br-td-green">{p.done_tasks}</td>
                      <td className="br-td-yellow">{p.pending_tasks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeSection === 'routines' && (
          <div className="br-card br-table-wrap">
            {routineCompletion.length === 0 ? (
              <div className="br-no-reportees">No routine data in this period.</div>
            ) : (
              <table className="br-table">
                <thead>
                  <tr>{['Routine', 'Area', 'Done', 'Total', 'Rate'].map(h => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {routineCompletion.map((r, i) => {
                    const rate = r.total ? Math.round((r.done / r.total) * 100) : 0;
                    const fillClass = rate >= 80 ? 'high' : rate >= 50 ? 'mid' : 'low';
                    return (
                      <tr key={i}>
                        <td>{r.description}</td>
                        <td className="br-td-muted">{r.area_name || '—'}</td>
                        <td className="br-td-green">{r.done}</td>
                        <td>{r.total}</td>
                        <td>
                          <div className="br-completion-wrap">
                            <div className="br-completion-bar">
                              <div className={`br-completion-fill ${fillClass}`} style={{ width: `${rate}%` }} />
                            </div>
                            <span className="br-completion-label">{rate}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeSection === 'action-items' && (
          <div className="br-card">
            <ActionItemsPanel reviewId={review.id} actionItems={actionItems} closed={closed} appId={appId} reportees={reportees} onRefresh={load} />
          </div>
        )}

        {activeSection === 'notes' && (
          <div className="br-card">
            {closed ? (
              <div>
                {review.meeting_notes && (
                  <div className="br-notes-section">
                    <div className="br-notes-label">Meeting Notes</div>
                    <div className="br-notes-body">{review.meeting_notes}</div>
                  </div>
                )}
                {review.summary && (
                  <div className="br-notes-section">
                    <div className="br-notes-label">Summary</div>
                    <div className="br-notes-body">{review.summary}</div>
                  </div>
                )}
                {review.key_risks && (
                  <div className="br-notes-section">
                    <div className="br-notes-label">Key Risks</div>
                    <div className="br-notes-body">{review.key_risks}</div>
                  </div>
                )}
                {review.achievements && (
                  <div className="br-notes-section">
                    <div className="br-notes-label">Achievements</div>
                    <div className="br-notes-body">{review.achievements}</div>
                  </div>
                )}
                {review.improvement_plans && (
                  <div className="br-notes-section">
                    <div className="br-notes-label">Improvement Plans</div>
                    <div className="br-notes-body">{review.improvement_plans}</div>
                  </div>
                )}
                {review.next_period_focus && (
                  <div className="br-notes-section">
                    <div className="br-notes-label">Next Period Focus</div>
                    <div className="br-notes-body">{review.next_period_focus}</div>
                  </div>
                )}
              </div>
            ) : (
              <NotesDraftPanel review={review} appId={appId} onRefresh={load} />
            )}
          </div>
        )}
      </div>

      {showClose && (
        <CloseReviewModal
          review={review}
          appId={appId}
          onClose={() => setShowClose(false)}
          onClosed={load}
        />
      )}
    </div>
  );
}

// ── Notes Draft (open review) ─────────────────────────────────────────────────

function NotesDraftPanel({ review, appId, onRefresh }) {
  const api = useApi();
  const [form, setForm] = useState({ meeting_notes: review.meeting_notes || '', duration_minutes: review.duration_minutes || '' });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.updateBusinessReview(appId, review.id, {
        meeting_notes: form.meeting_notes,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
      });
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <TextArea label="Meeting Notes" value={form.meeting_notes} onChange={v => setForm(f => ({ ...f, meeting_notes: v }))} rows={5} />
      <Input label="Duration (minutes)" type="number" value={String(form.duration_minutes || '')} onChange={v => setForm(f => ({ ...f, duration_minutes: v }))} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="br-btn br-btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Notes'}
        </button>
      </div>
    </div>
  );
}

// ── Reports Tab ───────────────────────────────────────────────────────────────

function ReportsTab({ appId, onOpenReview }) {
  const api = useApi();
  const [filters, setFilters] = useState({ type: '', status: 'closed', from: '', to: '' });
  const [managerFilter, setManagerFilter] = useState('');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    const params = { per_page: 100 };
    if (filters.type === 'daily') params.type = 'daily';
    if (filters.status) params.status = filters.status;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    api.listBusinessReviews(appId, params)
      .then(d => setReviews(d.reviews || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [appId, filters]);

  useEffect(() => { load(); }, [load]);

  const managerOptions = useMemo(() => {
    const map = new Map();
    for (const r of reviews) {
      if (r.manager_user_id && r.manager?.name) map.set(r.manager_user_id, r.manager.name);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [reviews]);

  const visibleReviews = reviews
    .filter(r => filters.type !== 'other' || r.type !== 'daily')
    .filter(r => !managerFilter || String(r.manager_user_id) === String(managerFilter));

  return (
    <div>
      <div className="br-filter-bar">
        <div className="br-filter-group">
          <label className="br-label">Type</label>
          <select className="br-filter-select" value={filters.type} onChange={e => setFilter('type', e.target.value)}>
            <option value="">All Types</option>
            <option value="daily">Daily Check-In</option>
            <option value="other">Other Review</option>
          </select>
        </div>
        <div className="br-filter-group">
          <label className="br-label">Status</label>
          <select className="br-filter-select" value={filters.status} onChange={e => setFilter('status', e.target.value)}>
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="br-filter-group">
          <label className="br-label">From</label>
          <input className="br-filter-input" type="date" value={filters.from} onChange={e => setFilter('from', e.target.value)} />
        </div>
        <div className="br-filter-group">
          <label className="br-label">To</label>
          <input className="br-filter-input" type="date" value={filters.to} onChange={e => setFilter('to', e.target.value)} />
        </div>
        {managerOptions.length > 0 && (
          <div className="br-filter-group">
            <label className="br-label">Manager</label>
            <SearchableSelect
              value={managerFilter}
              onChange={setManagerFilter}
              options={managerOptions.map(m => ({ value: m.id, label: m.name }))}
              placeholder="All Managers"
              dropdownWidth={200}
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="br-loading">Loading…</div>
      ) : visibleReviews.length === 0 ? (
        <div className="br-empty">No reviews match your filters.</div>
      ) : (
        <div className="br-card br-table-wrap">
          <div className="br-results-count">{visibleReviews.length} reviews</div>
          <table className="br-table">
            <thead>
              <tr>{['Type', 'Date', 'Period', 'Manager', 'Status', 'Duration', ''].map(h => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {visibleReviews.map(r => (
                <tr key={r.id}>
                  <td><Badge color="blue">{TYPE_LABEL[r.type]}</Badge></td>
                  <td className="br-td-bold">{fmt(r.review_date)}</td>
                  <td className="br-td-muted">{fmt(r.period_start)} — {fmt(r.period_end)}</td>
                  <td>{r.manager?.name || '—'}</td>
                  <td><Badge color={statusColor(r.status)}>{r.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</Badge></td>
                  <td className="br-td-muted">{r.duration_minutes ? `${r.duration_minutes} min` : '—'}</td>
                  <td>
                    <button className="br-btn br-btn-ghost br-btn-sm" onClick={() => onOpenReview(r.id, r.type)}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Employee Navigator Bar ────────────────────────────────────────────────────

function EmployeeNavBar({ uids, reporteeData, idx, onChange, reviewedSet = new Set() }) {
  const total    = uids.length;
  const uid      = uids[idx];
  const name     = reporteeData[uid]?.user?.name || `User ${uid}`;
  const reviewed = reviewedSet.size;

  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);

  const filtered = uids.filter(u => {
    const n = reporteeData[u]?.user?.name || '';
    return n.toLowerCase().includes(search.toLowerCase());
  });

  function pick(newUid) {
    const i = uids.indexOf(newUid);
    if (i >= 0) onChange(i);
    setOpen(false);
    setSearch('');
  }

  return (
    <div className="br-emp-nav">
      <button
        className="br-emp-nav-arrow"
        onClick={() => onChange(idx - 1)}
        disabled={idx === 0}
        title="Previous employee"
      >‹</button>

      <div className="br-emp-nav-center">
        <div className="br-emp-nav-name" onClick={() => setOpen(o => !o)} style={{ position: 'relative' }}>
          <span>{name}</span>
          <span className="br-emp-nav-count">{idx + 1} / {total}</span>
          <span className="br-emp-nav-chevron">▾</span>
          {open && (
            <div className="br-emp-nav-dropdown" onClick={e => e.stopPropagation()}>
              <input
                autoFocus
                className="br-emp-nav-search"
                placeholder="Search Employee…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
              <div className="br-emp-nav-list">
                {filtered.map((u, i) => (
                  <div
                    key={u}
                    className={`br-emp-nav-item${u === uid ? ' active' : ''}${reviewedSet.has(u) ? ' done' : ''}`}
                    onClick={() => pick(u)}
                  >
                    <span>{reporteeData[u]?.user?.name || `User ${u}`}</span>
                    {reviewedSet.has(u) && <span className="br-emp-nav-tick">✓</span>}
                  </div>
                ))}
                {filtered.length === 0 && <div className="br-emp-nav-empty">No match</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      <button
        className="br-emp-nav-arrow"
        onClick={() => onChange(idx + 1)}
        disabled={idx === total - 1}
        title="Next employee"
      >›</button>

      <div className="br-emp-nav-progress">
        <span className="br-emp-nav-prog-val" style={{ color: reviewed === total ? 'var(--pos,#22c55e)' : undefined }}>
          {reviewed}/{total}
        </span>
        <span className="br-emp-nav-prog-lbl">reviewed</span>
      </div>
    </div>
  );
}

// ── Daily Check-in View ───────────────────────────────────────────────────────

const DAILY_TASK_STATUS_OPTS = [
  { value: 'on_track',    label: 'On Track' },
  { value: 'need_support', label: 'Need Support' },
];

const TASK_STATUS_LABEL = { open: 'Not Started', in_progress: 'In Progress', blocked: 'Blocked', done: 'Done', cancelled: 'Cancelled' };
const TASK_STATUS_CLASS = { open: 'ts-todo', in_progress: 'ts-inprogress', blocked: 'ts-blocked', done: 'ts-done', cancelled: 'ts-cancelled' };
const REVIEW_STATUS_LABELS = {
  pending_self:     'Self Rating Pending',
  pending_manager:  'Awaiting Manager Review',
  pending_approver: 'With Final Approver',
};

const DAILY_STATUS_COLOR = {
  on_track: 'green',
  need_support: 'yellow',
  in_progress: 'yellow',
  open: 'gray',
  overdue: 'red',
};

export function DailyCheckinView({ reviewId, appId, onBack }) {
  const api = useApi();
  const { me } = useWorkspace();

  // Planner blocks: load from server keyed by user_id; use review date not today
  const [reviewDateKey, setReviewDateKey] = useState(null);
  const [serverPlannerBlocks, setServerPlannerBlocks] = useState({});

  const loadReporteePlannerBlocks = useCallback((reporteeData, dateKey) => {
    const uids = Object.keys(reporteeData || {});
    uids.forEach(uid => {
      api.getPlannerBlocks(appId, { user_id: uid, date: dateKey })
        .then(blocks => setServerPlannerBlocks(prev => ({ ...prev, [uid]: blocks || [] })))
        .catch(() => {});
    });
  }, [api, appId]);

  const plannedMinsForTask = useCallback((taskId, userId) => {
    if (!reviewDateKey) return 0;
    const blocks = serverPlannerBlocks[userId] || [];
    return blocks
      .filter(b => String(b.taskId) === String(taskId) && b.date === reviewDateKey)
      .reduce((s, b) => s + (b.endMinutes - b.startMinutes), 0);
  }, [serverPlannerBlocks, reviewDateKey]);

  const scheduledSlotsForTask = useCallback((taskId, userId) => {
    if (!reviewDateKey) return [];
    const toHHMM = (mins) => `${String(Math.floor(mins/60)).padStart(2,'0')}:${String(mins%60).padStart(2,'0')}`;
    return (serverPlannerBlocks[userId] || [])
      .filter(b => String(b.taskId) === String(taskId) && b.date === reviewDateKey)
      .sort((a, b) => a.startMinutes - b.startMinutes)
      .map(b => `${toHHMM(b.startMinutes)}–${toHHMM(b.endMinutes)}`);
  }, [serverPlannerBlocks, reviewDateKey]);

  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [taskStatuses, setTaskStatuses] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTaskComment, setNewTaskComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [remarks, setRemarks]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState(null);
  const [empIdx, setEmpIdx]         = useState(0);
  const [empComments, setEmpComments]     = useState({});   // { [uid]: string }
  const [collapsedCards, setCollapsedCards] = useState({}); // { [uid]: boolean }
  const [askAsQuery, setAskAsQuery]       = useState(false);
  const [queriesOpenFor, setQueriesOpenFor] = useState({}); // { [uid]: boolean }
  const [openQuery, setOpenQuery]         = useState(null);

  const applyData = useCallback((d) => {
    setData(d);
    const saved = {};
    for (const rd of Object.values(d.reporteeData || {})) {
      for (const t of rd.tasks || []) {
        if (t.review_status) saved[t.id] = t.review_status;
      }
    }
    setTaskStatuses(saved);
    setRemarks(d.review?.meeting_notes || '');
    if (d.review?.type === 'daily') {
      const dateKey = d.review.period_start || d.review.review_date;
      setReviewDateKey(dateKey);
      loadReporteePlannerBlocks(d.reporteeData, dateKey);
    }
  }, [loadReporteePlannerBlocks]);

  const load = useCallback(() => {
    setLoading(true);
    setEmpComments({});
    api.getBusinessReview(appId, reviewId)
      .then(applyData)
      .catch(() => setError('Failed to load review'))
      .finally(() => setLoading(false));
  }, [appId, reviewId, applyData]);

  const silentRefresh = useCallback(() => {
    api.getBusinessReview(appId, reviewId).then(applyData).catch(() => {});
  }, [appId, reviewId, applyData]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="br-page"><div className="br-loading">Loading…</div></div>;
  if (!data) return <div className="br-page"><div className="br-loading" style={{ color: 'var(--neg,#ef4444)' }}>{error || 'Not found'}</div></div>;

  const { review, manager, reporteeData } = data;
  const canEdit  = review.manager_user_id === me?.id;
  const closed   = review.status === 'closed';
  const editable = canEdit && !closed;
  const uids     = Object.keys(reporteeData || {});

  if (uids.length === 0) return (
    <div className="br-page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: 40, minHeight: 320 }}>
      <div style={{ background: 'rgba(240,169,59,0.12)', border: '1px solid rgba(240,169,59,0.35)', borderRadius: 12, padding: '28px 32px', maxWidth: 440, textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--warn)', marginBottom: 8 }}>No Team Members Found</div>
        <div style={{ fontSize: 14, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
          This review has no team members assigned. It may have been created before members were assigned to you.
          Please contact your administrator to assign team members.
        </div>
      </div>
      <button className="btn-back" onClick={onBack}>←</button>
    </div>
  );

  const todayStr = new Date().toISOString().slice(0, 10);

  async function handleSubmit() {
    if (!remarks.trim()) {
      setError('Remarks / comments are required to complete the review.');
      return;
    }
    if (remarks.length > 1000) {
      setError('Remarks must be under 1000 characters.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const taskStatusArray = Object.entries(taskStatuses).map(([taskId, status]) => ({
        task_id: parseInt(taskId), status,
      }));
      await api.saveDailyTaskStatuses(appId, reviewId, taskStatusArray);
      await api.updateBusinessReview(appId, reviewId, { meeting_notes: remarks });
      // save per-member comments typed before completing
      const commentEntries = Object.entries(empComments).filter(([, body]) => body.trim());
      await Promise.all(commentEntries.map(([uid, body]) =>
        api.addBRMemberComment(appId, reviewId, uid, body.trim())
      ));
      await api.closeBusinessReview(appId, reviewId, {});
      setSubmitted(true);
      silentRefresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="br-page">
      <div className="br-workspace-header">
        <button className="btn-back" onClick={onBack}>←</button>
        <div className="br-workspace-meta">
          <div className="br-workspace-title-row">
            <span className="br-workspace-title">Daily Check-In</span>
            <Badge color={statusColor(review.status)}>{review.status}</Badge>
          </div>
          <div className="br-workspace-subtitle">
            {fmt(review.review_date)} &nbsp;·&nbsp; Manager: {manager?.name}
          </div>
        </div>
        {editable && (
          <AddMemberButton appId={appId} reviewId={reviewId} currentUids={uids} onAdded={silentRefresh} />
        )}
      </div>

      {!canEdit && <div className="br-info-banner">This check-in can only be filled in by the manager.</div>}

      {uids.length === 0 ? (
        <div className="br-no-reportees">No team members found under this manager.</div>
      ) : (
        <div className="br-stack">
          {uids.map(uid => {
            const rd    = reporteeData[uid];
            const tasks = rd?.tasks || [];
            // Managers scrolling through many reportees default to collapsed; a lone
            // card (e.g. an employee viewing just their own check-in) stays expanded
            // since there's nothing else to declutter and collapsing it hides everything.
            const isCollapsed = uids.length > 1 ? collapsedCards[uid] !== false : collapsedCards[uid] === true;
            const completedPlannedMins = (rd?.completed_tasks || []).reduce((s, t) => s + plannedMinsForTask(t.id, uid), 0);
            const totalPlannedMins = tasks.reduce((s, t) => s + plannedMinsForTask(t.id, uid), 0) + completedPlannedMins;
            const completedTodayMins = (rd?.completed_tasks || []).reduce((s, t) => s + (Number(t.today_minutes) || 0), 0);
            const totalLoggedMins  = tasks.reduce((s, t) => s + (Number(t.today_minutes) || 0), 0) + completedTodayMins;
            const activeCount    = tasks.length;
            const completedCount = (rd?.completed_tasks || []).length;
            return (
              <div key={uid} className="br-checkin-card">
                <div
                  className="br-checkin-header br-checkin-header--clickable"
                  onClick={() => setCollapsedCards(s => ({ ...s, [uid]: !isCollapsed }))}
                >
                  <div className="br-checkin-header-left">
                    <span className={"br-checkin-toggle-arrow" + (isCollapsed ? " br-checkin-toggle-arrow--collapsed" : "")}>▾</span>
                    <div className="br-checkin-name">{rd?.user?.name || `User ${uid}`}</div>
                  </div>
                  <div className="br-checkin-stats">
                    <span className="br-checkin-stat">Planned: {totalPlannedMins > 0 ? fmtMins(totalPlannedMins) : '—'}</span>
                    <span className="br-checkin-stat">Logged: {totalLoggedMins > 0 ? fmtMins(totalLoggedMins) : '—'}</span>
                    <span className="br-checkin-stat">Active: {activeCount}</span>
                    <span className="br-checkin-stat">Completed: {completedCount}</span>
                    <span className="br-checkin-stat done">Done: {rd?.done_count}</span>
                    <span className={`br-checkin-stat ${rd?.overdue_count > 0 ? 'overdue' : ''}`}>
                      Overdue: {rd?.overdue_count}
                    </span>
                    <PendingQueriesChip queries={rd?.pending_queries} open={!!queriesOpenFor[uid]}
                      onToggle={() => setQueriesOpenFor(s => ({ ...s, [uid]: !s[uid] }))} />
                  </div>
                </div>

                {queriesOpenFor[uid] && <PendingQueriesList queries={rd?.pending_queries} onOpen={setOpenQuery} />}

                {isCollapsed ? null : tasks.length === 0 ? (
                  <div className="br-checkin-no-tasks">No pending tasks</div>
                ) : (
                  <div className="br-checkin-tasks" style={{ gap: 10, border: "none", background: "transparent", padding: 0 }}>
                    {tasks.map((t, ti) => {
                      const isOverdue = t.due_on && t.due_on < todayStr;
                      const plannedMins    = plannedMinsForTask(t.id, uid);
                      const scheduledSlots = scheduledSlotsForTask(t.id, uid);
                      const todayMins     = t.today_minutes || 0;
                      const actualMins    = t.actual_minutes || 0;
                      const estMins       = t.estimated_minutes || 0;
                      const yesterdayStatus = t.yesterday_review_status;
                      const yesterdayLabel  = DAILY_TASK_STATUS_OPTS.find(o => o.value === yesterdayStatus)?.label;
                      const yesterdayColor  = yesterdayStatus === 'on_track' ? '#16a34a' : yesterdayStatus === 'need_support' ? '#d97706' : null;
                      return (
                        <div key={t.id} className="br-daily-task-card">
                          {/* Row 1: S.No + title + status + review select */}
                          <div className="br-daily-task-row" onClick={() => setSelectedTask(t)}>
                            <span className="br-tdr-sno">{ti + 1}</span>
                            <span className="br-tdr-title">
                              {t.title}
                              {(t.comments || []).some(c => c.is_query && c.query_status !== 'closed') && <span className="br-tdr-query" title="Has a pending query">❓</span>}
                            </span>
                            <div className="br-tdr-right">
                              {t.status && <span className={"br-task-status-badge " + (TASK_STATUS_CLASS[t.status] || "")}>{TASK_STATUS_LABEL[t.status] || t.status}</span>}
                              <select
                                className={"br-task-status-select" + (taskStatuses[t.id] ? " status-" + taskStatuses[t.id] : "")}
                                value={taskStatuses[t.id] || ''}
                                onChange={e => editable && setTaskStatuses(s => ({ ...s, [t.id]: e.target.value }))}
                                disabled={!editable}
                                onClick={e => e.stopPropagation()}
                              >
                                <option value="">-- Review --</option>
                                {DAILY_TASK_STATUS_OPTS.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {/* Row 2: time & yesterday stats */}
                          <div className="br-daily-task-stats">
                            {scheduledSlots.length > 0 && (
                              <div className="br-dts-item">
                                <span className="br-dts-label">Scheduled</span>
                                <span className="br-dts-value sched" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {scheduledSlots.map((slot, si) => (
                                    <span key={si}>{slot}</span>
                                  ))}
                                </span>
                              </div>
                            )}
                            <div className="br-dts-item">
                              <span className="br-dts-label">Planned</span>
                              <span className={"br-dts-value" + (plannedMins > 0 ? " est" : " muted")}>
                                {plannedMins > 0 ? fmtMins(plannedMins) : '—'}
                              </span>
                            </div>
                            <div className="br-dts-item">
                              <span className="br-dts-label">Log Hrs</span>
                              <span className={"br-dts-value" + (todayMins > 0 ? (plannedMins > 0 && todayMins > plannedMins ? " over" : " spent") : " muted")}>
                                {todayMins > 0 ? fmtMins(todayMins) : '—'}
                              </span>
                            </div>
                            <div className="br-dts-item">
                              <span className="br-dts-label">Actual / Est</span>
                              <span className="br-dts-value muted">
                                {actualMins > 0 ? fmtMins(actualMins) : '0h'} / {estMins > 0 ? fmtMins(estMins) : '—'}
                              </span>
                            </div>
                            {yesterdayLabel && (
                              <div className="br-dts-item">
                                <span className="br-dts-label">Yesterday</span>
                                <span className="br-dts-value" style={{ color: yesterdayColor || '#64748b', fontWeight: 600 }}>
                                  {yesterdayLabel}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Completed tasks for today */}
                {!isCollapsed && (rd.completed_tasks || []).length > 0 && (
                  <div className="br-daily-completed-section">
                    <div className="br-daily-completed-label">Completed Today ({rd.completed_tasks.length})</div>
                    <div className="br-checkin-tasks" style={{ gap: 8, border: 'none', background: 'transparent', padding: 0 }}>
                      {rd.completed_tasks.map((t, ti) => (
                        <div key={t.id} className="br-daily-task-card br-daily-task-card--done" onClick={() => setSelectedTask(t)} style={{ cursor: 'pointer' }}>
                          <div className="br-daily-task-row">
                            <span className="br-tdr-sno" style={{ color: '#22c55e' }}>{ti + 1}</span>
                            <span className="br-tdr-title">{t.title}</span>
                            <div className="br-tdr-right">
                              <span className="br-task-status-badge status-done">Done</span>
                              {t.completed_at && (
                                <span className="br-daily-completed-time">
                                  {new Date(t.completed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manager comments for this member */}
                {!isCollapsed && (rd.member_comments || []).length > 0 && (
                  <div className="br-member-comments-list">
                    {(rd.member_comments || []).map(c => (
                      <div key={c.id} className="br-member-comment-item">
                        <span className="br-member-comment-author">{c.author?.name || 'Manager'}:</span>
                        <span className="br-member-comment-body">{c.body}</span>
                        <span className="br-member-comment-time">{new Date(c.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
                      </div>
                    ))}
                  </div>
                )}

                {!isCollapsed && editable && (
                  <div className="br-task-comment-row" onClick={e => e.stopPropagation()}>
                    <input
                      className="br-task-comment-input"
                      type="text"
                      placeholder="Add a comment for this employee…"
                      value={empComments[uid] || ''}
                      onChange={e => setEmpComments(s => ({ ...s, [uid]: e.target.value.slice(0, 1000) }))}
                      maxLength={1000}
                    />
                    <span className="br-char-count">{(empComments[uid] || '').length} / 1000</span>
                  </div>
                )}

              </div>
            );
          })}

          <div className="br-checkin-footer">
            {editable && (
              <div className="br-field">
                <label className="br-label">Remarks</label>
                <textarea
                  className="br-textarea"
                  rows={3}
                  placeholder="Overall remarks for today's check-in…"
                  value={remarks}
                  onChange={e => setRemarks(e.target.value.slice(0, 1000))}
                  maxLength={1000}
                />
                <span className={remarks.length > 1000 ? 'br-char-count br-char-count--over' : 'br-char-count'}>{remarks.length} / 1000</span>
              </div>
            )}
            {!editable && review.meeting_notes && (
              <div className="br-field">
                <label className="br-label">Remarks</label>
                <div className="br-monthly-notes-ro">{review.meeting_notes}</div>
              </div>
            )}
            {editable && (
              <div className="br-checkin-submit-row">
                {error && <span className="br-checkin-error">{error}</span>}
                {submitted && !submitting && (
                  <span className="br-checkin-saved">✓ All {uids.length} reviewed &amp; saved</span>
                )}
                <button className="br-btn br-btn-primary" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Saving…' : 'Complete Review'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {selectedTask && (() => {
        const t = selectedTask;
        const isOverdue = t.due_on && t.due_on < todayStr;
        const submitTaskComment = async () => {
          const body = newTaskComment.trim();
          if (!body || postingComment) return;
          setPostingComment(true);
          try {
            const saved = await api.addComment(appId, t.id, askAsQuery ? { body, is_query: true, business_review_id: Number(reviewId) } : { body });
            const authorName = [me?.firstName, me?.lastName].filter(Boolean).join(' ') || me?.email || 'You';
            const newComment = { id: saved.id, body: saved.body, created_at: saved.created_at, is_query: saved.is_query, query_status: saved.query_status, author: { name: authorName } };
            if (saved.is_query) { setAskAsQuery(false); silentRefresh(); }
            setSelectedTask(cur => cur ? { ...cur, comments: [...(cur.comments || []), newComment] } : cur);
            setData(prev => {
              if (!prev) return prev;
              const patchList = (list) => (list || []).map(x => x.id === t.id ? { ...x, comments: [...(x.comments || []), newComment] } : x);
              const reporteeData = {};
              for (const [uid, rd] of Object.entries(prev.reporteeData || {})) {
                reporteeData[uid] = { ...rd, tasks: patchList(rd.tasks), completed_tasks: patchList(rd.completed_tasks) };
              }
              return { ...prev, reporteeData };
            });
            setNewTaskComment('');
          } catch (err) {
            console.error(err);
          } finally {
            setPostingComment(false);
          }
        };
        return (
          <div className="br-modal-overlay" onClick={() => setSelectedTask(null)}>
            <div className="br-modal br-modal-2xl" onClick={e => e.stopPropagation()}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,marginBottom:16}}>
                <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',flex:1}}>
                  <div className="br-modal-title" style={{margin:0}}>{t.title}</div>
                  {t.status && <span className={"br-task-status-badge " + (TASK_STATUS_CLASS[t.status] || "")}>{TASK_STATUS_LABEL[t.status] || t.status}</span>}
                </div>
                <button className="br-task-modal-close" onClick={() => setSelectedTask(null)} type="button">&#x2715;</button>
              </div>
              {t.area_name && (
                <div className="br-task-modal-row">
                  <span className="br-task-modal-label">Area</span>
                  <span className="br-task-area">{t.area_name}</span>
                </div>
              )}
              {t.due_on && (
                <div className="br-task-modal-row">
                  <span className="br-task-modal-label">Due</span>
                  <span className={"br-tdc-due" + (isOverdue ? " overdue" : "")}>{fmt(t.due_on)}</span>
                </div>
              )}
              {t.completed_at && (
                <div className="br-task-modal-row">
                  <span className="br-task-modal-label">Completed On</span>
                  <span className="br-tdc-due" style={{ color: '#22c55e' }}>{new Date(t.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
              )}
              {(t.estimated_minutes > 0 || t.actual_minutes > 0) && (
                <div className="br-task-modal-row">
                  <span className="br-task-modal-label">Time</span>
                  <div className="br-tdc-time-row" style={{margin: 0}}>
                    {t.estimated_minutes > 0 && (
                      <div className="br-tdc-time-chip">
                        <span className="br-tdc-time-label">Estimation</span>
                        <span className="br-tdc-time-val">{fmtMins(t.estimated_minutes)}</span>
                      </div>
                    )}
                    {t.actual_minutes > 0 && (
                      <div className={"br-tdc-time-chip" + (t.actual_minutes > t.estimated_minutes && t.estimated_minutes > 0 ? " over" : "")}>
                        <span className="br-tdc-time-label">Time Spent</span>
                        <span className="br-tdc-time-val">{fmtMins(t.actual_minutes)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {t.description && (
                <div className="br-task-modal-desc">
                  <span className="br-task-modal-label">Description</span>
                  <div className="br-tdc-description" style={{marginTop: 4}}>{t.description}</div>
                </div>
              )}
              {t.comments && t.comments.length > 0 && (() => {
                const LIMIT = 5;
                const expanded = expandedComments[t.id];
                const shown = expanded ? t.comments : t.comments.slice(0, LIMIT);
                const hasMore = t.comments.length > LIMIT;
                return (
                  <div className="br-tdc-comments" style={{marginTop: 12}}>
                    <div className="br-tdc-comments-label">Comments ({t.comments.length})</div>
                    {shown.map(c => (
                      <div key={c.id} className="br-tdc-comment">
                        <div className="br-tdc-comment-avatar">{(c.author?.name || "?")[0].toUpperCase()}</div>
                        <div className="br-tdc-comment-body">
                          <div className="br-tdc-comment-meta">
                            <span className="br-tdc-comment-author">{c.author?.name || "Unknown"}</span>
                            <span className="br-tdc-comment-time">{fmt(c.created_at)}</span>{c.is_query && <QueryBadge status={c.query_status} />}
                          </div>
                          <div className="br-tdc-comment-text">{c.body}</div>
                        </div>
                      </div>
                    ))}
                    {!expanded && hasMore && (
                      <button className="br-tdc-comments-viewmore" onClick={() => setExpandedComments(s => ({...s, [t.id]: true}))} type="button">View more comments</button>
                    )}
                    {expanded && hasMore && (
                      <button className="br-tdc-comments-showless" onClick={() => setExpandedComments(s => ({...s, [t.id]: false}))} type="button">Show less</button>
                    )}
                  </div>
                );
              })()}
              {editable && (
                <div className="br-task-comment-row" style={{ marginTop: 12 }}>
                  <input
                    className="br-task-comment-input"
                    type="text"
                    placeholder={askAsQuery ? "Ask a question the assignee must answer…" : "Add a comment for this task…"}
                    value={newTaskComment}
                    onChange={e => setNewTaskComment(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitTaskComment(); }}
                  />
                  <AskAsQueryToggle checked={askAsQuery} onChange={setAskAsQuery} />
                  <button
                    className="br-btn br-btn-primary br-btn-sm"
                    onClick={submitTaskComment}
                    disabled={postingComment || !newTaskComment.trim()}
                    type="button"
                  >
                    {postingComment ? 'Posting…' : 'Post'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {openQuery && <QueryModal appId={appId} query={openQuery} onClose={() => setOpenQuery(null)} onChanged={silentRefresh} />}
    </div>
  );
}

// ── Weekly Check-in View ─────────────────────────────────────────────────────

function fmtMins(mins) {
  if (!mins) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + 'm' : ''}`.trim() : `${m}m`;
}

export function WeeklyCheckinView({ reviewId, appId, onBack }) {
  const api = useApi();
  const { me } = useWorkspace();

  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [drafts, setDrafts]         = useState({});
  const [remarks, setRemarks]       = useState('');
  const [saving, setSaving]         = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState(null);
  const [modal, setModal]           = useState(null);
  const [modalForm, setModalForm]   = useState({});
  const [sectionModal, setSectionModal] = useState(null); // { uid, key } | null
  const [taskModal, setTaskModal] = useState(null);
  const [taskDetailModal, setTaskDetailModal] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [newTaskComment, setNewTaskComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [createTaskFor, setCreateTaskFor] = useState(null); // uid | null
  const [askAsQuery, setAskAsQuery]       = useState(false);
  const [queriesOpenFor, setQueriesOpenFor] = useState({}); // { [uid]: boolean }
  const [openQuery, setOpenQuery]         = useState(null);
  const [members, setMembers]         = useState([]);
  const [roles, setRoles]             = useState([]);
  const [areasByRole, setAreasByRole] = useState(new Map());

  useEffect(() => {
    if (!appId) return;
    Promise.all([
      api.listAppMembers(appId).catch(() => ({ items: [] })),
      api.roles.list(appId).catch(() => ({ items: [] })),
    ]).then(async ([m, r]) => {
      const memberRows = m.items || [];
      const roleRows   = r.items || [];
      setMembers(memberRows);
      setRoles(roleRows);
      const perRole = await Promise.all(
        roleRows.map(role =>
          api.listAreas(appId, role.id).then(res => ({ role, items: res.items || [] })).catch(() => ({ role, items: [] }))
        )
      );
      const map = new Map();
      for (const { role, items } of perRole) map.set(role.id, items);
      setAreasByRole(map);
    }).catch(() => {});
  }, [api, appId]);

  const applyData = useCallback((d) => {
    setData(d);
    const initDrafts = {};
    for (const [uid, rd] of Object.entries(d.reporteeData || {})) {
      const a = rd.assessment;
      if (a) {
        initDrafts[uid] = {
          overall_rating:    a.overall_rating    || '',
          score:             a.score != null ? Number(a.score) : null,
          task_notes:        a.task_notes        || '',
          project_notes:     a.project_notes     || '',
          goal_notes:        a.goal_notes        || '',
          recognition_notes: a.recognition_notes || '',
          development_notes: a.development_notes || '',
          comment:           a.comment           || '',
        };
      }
    }
    setDrafts(initDrafts);
    setRemarks(d.review?.meeting_notes || '');
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.getBusinessReview(appId, reviewId)
      .then(applyData)
      .catch(() => setError('Failed to load review'))
      .finally(() => setLoading(false));
  }, [appId, reviewId, applyData]);

  const silentRefresh = useCallback(() => {
    api.getBusinessReview(appId, reviewId).then(applyData).catch(() => {});
  }, [appId, reviewId, applyData]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setNewTaskComment(''); }, [taskDetailModal?.id]);

  async function submitTaskDetailComment() {
    const body = newTaskComment.trim();
    if (!body || !taskDetailModal || postingComment) return;
    setPostingComment(true);
    try {
      const saved = await api.addComment(appId, taskDetailModal.id, askAsQuery ? { body, is_query: true, business_review_id: Number(reviewId) } : { body });
      const authorName = [me?.firstName, me?.lastName].filter(Boolean).join(' ') || me?.email || 'You';
      const newComment = { id: saved.id, body: saved.body, created_at: saved.created_at, is_query: saved.is_query, query_status: saved.query_status, author: { name: authorName } };
      if (saved.is_query) { setAskAsQuery(false); silentRefresh(); }
      setTaskDetailModal(cur => cur ? { ...cur, comments: [...(cur.comments || []), newComment] } : cur);
      setNewTaskComment('');
    } catch (err) {
      console.error(err);
    } finally {
      setPostingComment(false);
    }
  }

  const setDraft = (uid, field, value) =>
    setDrafts(prev => ({ ...prev, [uid]: { ...(prev[uid] || {}), [field]: value } }));

  async function saveMember(uid) {
    setSaving(s => ({ ...s, [uid]: true }));
    try {
      await api.upsertBRAssessment(appId, reviewId, uid, drafts[uid] || {});
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(s => ({ ...s, [uid]: false }));
    }
  }

  async function handleSubmit() {
    if (!remarks.trim()) {
      setError('Remarks are required to complete the review.');
      return;
    }
    if (remarks.length > 1000) {
      setError('Remarks must be under 1000 characters.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const uids = Object.keys(data.reporteeData || {});
      await Promise.all(uids.map(uid => api.upsertBRAssessment(appId, reviewId, uid, drafts[uid] || {})));
      await api.updateBusinessReview(appId, reviewId, { meeting_notes: remarks });
      await api.closeBusinessReview(appId, reviewId, {});
      setSaved(true);
      silentRefresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitAppreciation() {
    if (!modalForm.message?.trim()) {
      setModalForm(f => ({ ...f, err: 'Please write a message' }));
      return;
    }
    try {
      await api.giveAppreciation(appId, {
        to_user_id: parseInt(modal.uid),
        message: modalForm.message.trim(),
        badge: modalForm.badge || null,
      });
      setModal(null); setModalForm({});
    } catch (err) {
      setModalForm(f => ({ ...f, err: err.response?.data?.error || 'Failed to send' }));
    }
  }

  async function submitFeedback() {
    if (!modalForm.feedback_text?.trim()) {
      setModalForm(f => ({ ...f, err: 'Please write your feedback' }));
      return;
    }
    try {
      await api.giveFeedback(appId, {
        to_user_id: parseInt(modal.uid),
        feedback_text: modalForm.feedback_text.trim(),
        is_anonymous: modalForm.is_anonymous ? 1 : 0,
      });
      setModal(null); setModalForm({});
    } catch (err) {
      setModalForm(f => ({ ...f, err: err.response?.data?.error || 'Failed to send' }));
    }
  }

  if (loading) return <div className="br-page"><div className="br-loading">Loading…</div></div>;
  if (!data) return (
    <div className="br-page">
      <div className="br-loading" style={{ color: 'var(--neg,#ef4444)' }}>{error || 'Not found'}</div>
    </div>
  );

  const { review, manager, reporteeData } = data;
  const canEdit  = review.manager_user_id === me?.id;
  const closed   = review.status === 'closed';
  const editable = canEdit && !closed;
  const uids     = Object.keys(reporteeData || {});

  if (uids.length === 0) return (
    <div className="br-page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: 40, minHeight: 320 }}>
      <div style={{ background: 'rgba(240,169,59,0.12)', border: '1px solid rgba(240,169,59,0.35)', borderRadius: 12, padding: '28px 32px', maxWidth: 440, textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--warn)', marginBottom: 8 }}>No Team Members Found</div>
        <div style={{ fontSize: 14, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
          This review has no team members assigned. It may have been created before members were assigned to you.
          Please contact your administrator to assign team members.
        </div>
      </div>
      <button className="btn-back" onClick={onBack}>←</button>
    </div>
  );

  const periodLabel = `${fmt(review.period_start)} — ${fmt(review.period_end)}`;

  return (
    <div className="br-page">
      <div className="br-workspace-header">
        <button className="btn-back" onClick={onBack}>←</button>
        <div className="br-workspace-meta">
          <div className="br-workspace-title-row">
            <span className="br-workspace-title">Weekly Review</span>
            <Badge color={statusColor(review.status)}>{review.status}</Badge>
          </div>
          <div className="br-workspace-subtitle">
            {periodLabel} &nbsp;&middot;&nbsp; Manager: {manager?.name}
          </div>
        </div>
        {editable && (
          <AddMemberButton appId={appId} reviewId={reviewId} currentUids={uids} onAdded={silentRefresh} />
        )}
      </div>

      {!canEdit && (
        <div className="br-info-banner">This review can only be filled in by the manager.</div>
      )}

      {uids.length === 0 ? (
        <div className="br-empty">No team members found under this manager.</div>
      ) : (
        <div className="br-rc-list">
          {uids.map(uid => {
            const rd  = reporteeData[uid] || {};
            const d   = drafts[uid] || {};
            const s   = rd.summary || {};
            const taskHrsH = Math.floor((s.task_hours_minutes || 0) / 60);
            const taskHrsM = (s.task_hours_minutes || 0) % 60;
            const taskHrsLabel = taskHrsH > 0 ? `${taskHrsH}h ${taskHrsM}m` : `${taskHrsM}m`;
            const projects = s.projects || [];
            const contracts = s.contract_tasks || [];
            const perfReviews = s.perf_reviews || [];
            const goalsMap = s.goals_map || {};
            const goalsTotal = s.goals_total || 0;
            const routinesDone = Array.isArray(s.routines_done) ? s.routines_done.length : (s.routines_done || 0);
            const routinesPending = Array.isArray(s.routines_pending) ? s.routines_pending.length : (s.routines_pending || 0);
            const apprReceived = Array.isArray(s.appr_received) ? s.appr_received.length : (s.appr_received || 0);
            const apprGiven = Array.isArray(s.appr_given) ? s.appr_given.length : (s.appr_given || 0);
            const fbReceived = Array.isArray(s.feedback_received) ? s.feedback_received.length : (s.feedback_received || 0);
            const fbGiven = Array.isArray(s.feedback_given) ? s.feedback_given.length : (s.feedback_given || 0);
            const nameInitials = (rd.user?.name || `U${uid}`).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            const AVATAR_COLORS_W = ['#6366f1','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ef4444'];
            const avatarColorW = AVATAR_COLORS_W[(rd.user?.name || '').charCodeAt(0) % AVATAR_COLORS_W.length] || '#6366f1';
            const wTotalTasks = (s.completed_tasks || 0) + (s.pending_tasks_count || 0) + (s.on_hold_tasks || 0) + (s.overdue_tasks || 0);
            const wOnTimePct = wTotalTasks > 0 ? Math.round(((s.on_time_tasks || 0) / wTotalTasks) * 100) : 0;
            const perfPending = s.performance_pending || {};
            const reviewCreatedTasks = s.review_created_tasks || [];
            const carriedForwardTasks = s.carried_forward_tasks || [];
            return (
          <div key={uid} className="br-mnew-card">

            {/* Profile Header + Stats inline */}
            <div className="br-mnew-header">
              <div className="br-mnew-avatar" style={{ background: avatarColorW }}>{nameInitials}</div>
              <div className="br-mnew-identity">
                <div className="br-mnew-name">{rd.user?.name || `User ${uid}`}</div>
                {rd.user?.role_title && <div className="br-mnew-role">{rd.user.role_title}</div>}
                {rd.user?.employee_id && <div className="br-mnew-empid">Employee ID: GLOS-{rd.user.employee_id}</div>}
              </div>
              <div className="br-mnew-header-stats">
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{taskHrsLabel}</span><span className="br-mnew-stat-lbl">Hours Logged</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{s.completed_tasks || 0}</span><span className="br-mnew-stat-lbl">Tasks Completed</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{s.pending_tasks_count || 0}</span><span className="br-mnew-stat-lbl">Pending Tasks</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{wOnTimePct}%</span><span className="br-mnew-stat-lbl">On-Time Delivery</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{perfReviews.length}</span><span className="br-mnew-stat-lbl">Reviews</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val br-mnew-trend-up">+{routinesDone}</span><span className="br-mnew-stat-lbl">Routines Done</span></div>
              </div>
              <PendingQueriesChip queries={rd.pending_queries} open={!!queriesOpenFor[uid]}
                onToggle={() => setQueriesOpenFor(s => ({ ...s, [uid]: !s[uid] }))} />
              {editable && (
                <button className="br-btn br-btn-secondary br-btn-sm" style={{ marginTop: 5 }} onClick={() => setCreateTaskFor(uid)}>+ Create Task</button>
              )}
            </div>

            {queriesOpenFor[uid] && <PendingQueriesList queries={rd.pending_queries} onOpen={setOpenQuery} />}

            {/* Still-open tasks carried forward from any earlier review —
                keeps reappearing every review until actually done, not just
                once right after it was created, so a task that's still
                unstarted 3-4 reviews later doesn't quietly fall out of view. */}
            <ReviewTasksPanel tasks={carriedForwardTasks} onTaskClick={setTaskDetailModal}
              title="Carried Forward — Not Yet Done" showLatestComment />

            {/* Tasks created in this review */}
            <ReviewTasksPanel tasks={reviewCreatedTasks} onTaskClick={setTaskDetailModal} />

            {/* Two-column: Tasks Overview (left) + Collapsible sections (right) */}
            <div className="br-mnew-main-row">
              <div className="br-mnew-tasks-panel">
                <div className="br-mnew-panel-title">Tasks Overview</div>
                <table className="br-mnew-task-table">
                  <thead>
                    <tr><th>Status</th><th>Count</th><th>Percentage</th><th></th></tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Completed', count: s.completed_tasks || 0, color: '#22c55e', list: s.completed_task_list || [] },
                      { label: 'Pending',   count: s.pending_tasks_count || 0, color: '#f59e0b', list: s.pending_task_list || [] },
                      { label: 'Blocked',   count: s.on_hold_tasks || 0, color: '#6366f1', list: s.on_hold_task_list || [] },
                      { label: 'Overdue',   count: s.overdue_tasks || 0, color: '#ef4444', list: s.overdue_task_list || [] },
                    ].map(row => {
                      const pct = wTotalTasks > 0 ? Math.round((row.count / wTotalTasks) * 100) : 0;
                      return (
                        <tr key={row.label}>
                          <td><span className="br-mnew-dot" style={{ background: row.color }} />{row.label}</td>
                          <td>{row.count}</td>
                          <td>
                            <div className="br-mnew-bar-wrap">
                              <div className="br-mnew-bar-fill" style={{ width: `${pct}%`, background: row.color }} />
                            </div>
                            <span className="br-mnew-bar-pct">{pct}%</span>
                          </td>
                          <td>
                            {row.count > 0 && (
                              <button className="br-mnew-view-btn" onClick={() => setTaskModal({ title: row.label, color: row.color, tasks: row.list })}>View</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <ReviewHistorySection appId={appId} reviewId={review.id} review={review} userId={Number(uid)} userName={rd.user?.name || `User ${uid}`} />
              </div>
              <div className="br-mnew-coll-panel">
                {[
                  { key: 'projects', icon: '📁', label: 'Projects', count: projects.length, content: (
                    projects.length === 0 ? <p className="br-rc-empty-hint">No projects this period.</p> :
                    <div className="br-rc-item-list">{projects.map(p => (
                      <div key={p.id} className="br-rc-item">
                        <div className={`br-rc-item-dot br-rc-item-dot--${p.is_completed ? 'done' : 'active'}`} />
                        <span className="br-rc-item-name">{p.name}</span>
                        <span className="br-rc-item-meta">{p.start_date ? fmt(p.start_date) : '—'} → {p.end_date ? fmt(p.end_date) : '—'}</span>
                        <span className={`br-rc-item-badge br-rc-item-badge--${p.is_completed ? 'done' : 'active'}`}>{p.is_completed ? 'Completed' : 'Active'}</span>
                      </div>
                    ))}</div>
                  )},
                  { key: 'contracts', icon: '📄', label: 'Agreements', count: contracts.length, content: (
                    contracts.length === 0 ? <p className="br-rc-empty-hint">No agreements this period.</p> :
                    <div className="br-rc-item-list">{contracts.map((c, ci) => (
                      <div key={ci} className="br-rc-item">
                        <div className={`br-rc-item-dot br-rc-item-dot--${c.status === 'done' ? 'done' : 'active'}`} />
                        <span className="br-rc-item-name">{c.service_name || c.client_name || 'Agreement'}</span>
                        <span className="br-rc-item-meta">{c.start_date ? fmt(c.start_date) : '—'} → {c.end_date ? fmt(c.end_date) : '—'}</span>
                        <span className={`br-rc-item-badge br-rc-item-badge--${c.status === 'done' ? 'done' : 'active'}`}>{c.status || 'active'}</span>
                      </div>
                    ))}</div>
                  )},
                  { key: 'feedback', icon: '💬', label: 'Feedback & Appreciation', count: fbReceived + apprReceived, content: (
                    <div className="br-mnew-fb-grid">
                      <div className="br-mnew-fb-stat"><span>{apprReceived}</span><small>Appr. Received</small></div>
                      <div className="br-mnew-fb-stat"><span>{apprGiven}</span><small>Appr. Given</small></div>
                      <div className="br-mnew-fb-stat"><span>{fbReceived}</span><small>Feedback Received</small></div>
                      <div className="br-mnew-fb-stat"><span>{fbGiven}</span><small>Feedback Given</small></div>
                    </div>
                  )},
                  { key: 'routines', icon: '🔄', label: 'Role Routines', count: routinesDone + routinesPending, content: (
                    <div className="br-mnew-fb-grid">
                      <div className="br-mnew-fb-stat"><span>{routinesDone}</span><small>Completed</small></div>
                      <div className="br-mnew-fb-stat"><span>{routinesPending}</span><small>Pending</small></div>
                    </div>
                  )},
                  { key: 'performance', icon: '📈', label: 'Performance', count: (perfPending.review_cycles?.length || 0) + (perfPending.pending_goals_count || 0), content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div className="br-mnew-fb-grid">
                        <div className="br-mnew-fb-stat"><span>{perfPending.review_cycles?.length || 0}</span><small>Reviews Pending</small></div>
                        <div className="br-mnew-fb-stat"><span>{perfPending.pending_goals_count || 0}</span><small>Goals Pending</small></div>
                      </div>
                      {(perfPending.review_cycles || []).length > 0 && (
                        <div className="br-rc-item-list">{perfPending.review_cycles.map(rc => (
                          <div key={rc.id} className="br-rc-item">
                            <div className="br-rc-item-dot br-rc-item-dot--active" />
                            <span className="br-rc-item-name">{rc.cycle_name}</span>
                            <span className={`br-rc-item-badge br-rc-item-badge--active`}>{REVIEW_STATUS_LABELS[rc.status] || rc.status}</span>
                          </div>
                        ))}</div>
                      )}
                    </div>
                  )},
                ].map(sec => (
                  <div key={sec.key} className="br-mnew-coll">
                    <button className="br-mnew-coll-header" onClick={() => setSectionModal({ uid, key: sec.key })}>
                      <span className="br-mnew-coll-icon">{sec.icon}</span>
                      <span className="br-mnew-coll-label">{sec.label}</span>
                      <span className="br-mnew-coll-count">{sec.count} {sec.label.split(' ')[0]}{sec.count !== 1 ? 's' : ''}</span>
                      <span className="br-mnew-coll-arrow">›</span>
                    </button>
                    <Modal
                      open={sectionModal?.uid === uid && sectionModal?.key === sec.key}
                      title={sec.label}
                      onClose={() => setSectionModal(null)}
                    >
                      {sec.content}
                    </Modal>
                  </div>
                ))}
              </div>
            </div>

            {/* Manager Assessment — full width below */}
            <div className="br-mnew-assess-full">
              <div className="br-mnew-assess-row">
                <div style={{ flex: 1 }}>
                  <div className="br-mnew-comment-label">Manager Comments</div>
                  {editable ? (
                    <textarea className="br-mnew-comment" placeholder="Write your feedback, observations and areas for growth..."
                      value={d.comment || ''} onChange={e => setDraft(uid, 'comment', e.target.value)} maxLength={1000} />
                  ) : (
                    d.comment ? <div className="br-monthly-notes-ro">{d.comment}</div> : <span className="br-rc-empty-hint">—</span>
                  )}
                </div>
              </div>
              {editable && (
                <div className="br-mnew-footer-row">
                  <button className="br-btn br-btn-ghost br-btn-sm"
                    onClick={() => { setModal({ kind: 'appreciation', uid }); setModalForm({ badge: 'star' }); }}>
                    🙌 Appreciation
                  </button>
                  <button className="br-btn br-btn-ghost br-btn-sm"
                    onClick={() => { setModal({ kind: 'feedback', uid }); setModalForm({}); }}>
                    💬 Feedback
                  </button>
                  <button className="br-btn br-btn-primary br-btn-sm" disabled={saving[uid]}
                    style={{ marginLeft: 'auto' }}
                    onClick={() => saveMember(uid)}>
                    {saving[uid] ? 'Saving…' : 'Save Review'}
                  </button>
                </div>
              )}
            </div>

          </div>
            );
          })}

          {editable && (
            <div className="br-monthly-card">
              <div className="br-monthly-section-title">Remarks <span className="br-required">*</span></div>
              <textarea className="br-mnew-comment" placeholder="Overall remarks for this week's review..."
                value={remarks} onChange={e => setRemarks(e.target.value.slice(0, 1000))} rows={3} maxLength={1000} />
              <span className={remarks.length > 1000 ? 'br-char-count br-char-count--over' : 'br-char-count'}>{remarks.length} / 1000</span>
              <div className="br-checkin-submit-row" style={{ marginTop: 12 }}>
                {error && <span className="br-checkin-error">{error}</span>}
                {saved && !submitting && (
                  <span className="br-checkin-saved">✓ All {uids.length} reviewed &amp; saved</span>
                )}
                <button className="br-btn br-btn-primary" disabled={submitting} onClick={handleSubmit}>
                  {submitting ? 'Saving...' : 'Complete Review'}
                </button>
              </div>
            </div>
          )}
          {!editable && review.meeting_notes && (
            <div className="br-monthly-card">
              <div className="br-monthly-section-title">Remarks</div>
              <div className="br-monthly-notes-ro">{review.meeting_notes}</div>
            </div>
          )}
        </div>
      )}

      {taskModal && (
        <Modal open size="lg" title={`${taskModal.title} Tasks`} onClose={() => setTaskModal(null)}>
          <div className="br-tmodal-wrap">
            {taskModal.tasks.length === 0 ? (
              <p className="br-rc-empty-hint">No task details available.</p>
            ) : (
              <table className="br-tmodal-table">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Priority</th>
                    <th>Due Date</th>
                    <th>Area</th>
                    {taskModal.title === 'Completed' && <th>Completed On</th>}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {taskModal.tasks.map(t => (
                    <tr key={t.id}
                      onClick={taskModal.title === 'Completed' ? () => { setTaskDetailModal(t); setTaskModal(null); } : undefined}
                      style={taskModal.title === 'Completed' ? { cursor: 'pointer' } : undefined}
                    >
                      <td className="br-tmodal-title">{t.title}</td>
                      <td><span className={`br-tmodal-priority br-tmodal-priority--${t.priority || 'normal'}`}>{t.priority || 'normal'}</span></td>
                      <td className="br-tmodal-date">{t.due_on ? new Date(t.due_on).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}</td>
                      <td className="br-tmodal-area">{t.area_name || '—'}</td>
                      {taskModal.title === 'Completed' && (
                        <td className="br-tmodal-date">{t.completed_at ? new Date(t.completed_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}</td>
                      )}
                      <td><span className="br-tmodal-status" style={{ background: taskModal.color + '22', color: taskModal.color }}>{TASK_STATUS_LABEL[t.status] || t.status?.replace(/_/g,' ')}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Modal>
      )}

      {taskDetailModal && (() => {
        const t = taskDetailModal;
        return (
          <Modal
            open
            size="lg"
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {t.title}
                {t.status && <span className={"br-task-status-badge " + (TASK_STATUS_CLASS[t.status] || '')}>{TASK_STATUS_LABEL[t.status] || t.status}</span>}
              </span>
            }
            onClose={() => setTaskDetailModal(null)}
          >
            <div className="br-task-modal-body">
              {t.area_name && <div className="br-task-modal-row"><span className="br-task-modal-label">Area</span><span className="br-task-area">{t.area_name}</span></div>}
              {t.due_on && <div className="br-task-modal-row"><span className="br-task-modal-label">Due</span><span className="br-tdc-due">{fmt(t.due_on)}</span></div>}
              {t.completed_at && <div className="br-task-modal-row"><span className="br-task-modal-label">Completed On</span><span className="br-tdc-due" style={{ color: '#22c55e' }}>{new Date(t.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>}
              {(t.estimated_minutes > 0 || t.actual_minutes > 0) && (
                <div className="br-task-modal-row">
                  <span className="br-task-modal-label">Time</span>
                  <div className="br-tdc-time-row" style={{ margin: 0 }}>
                    {t.estimated_minutes > 0 && <div className="br-tdc-time-chip"><span className="br-tdc-time-label">Estimation</span><span className="br-tdc-time-val">{fmtMins(t.estimated_minutes)}</span></div>}
                    {t.actual_minutes > 0 && <div className={"br-tdc-time-chip" + (t.actual_minutes > t.estimated_minutes && t.estimated_minutes > 0 ? " over" : "")}><span className="br-tdc-time-label">Time Spent</span><span className="br-tdc-time-val">{fmtMins(t.actual_minutes)}</span></div>}
                  </div>
                </div>
              )}
              {t.description && <div className="br-task-modal-desc"><span className="br-task-modal-label">Description</span><div className="br-tdc-description" style={{ marginTop: 4 }}>{t.description}</div></div>}
              {t.comments && t.comments.length > 0 && (() => {
                const LIMIT = 5;
                const expanded = expandedComments[t.id];
                const shown = expanded ? t.comments : t.comments.slice(0, LIMIT);
                const hasMore = t.comments.length > LIMIT;
                return (
                  <div className="br-tdc-comments" style={{ marginTop: 12 }}>
                    <div className="br-tdc-comments-label">Comments ({t.comments.length})</div>
                    {shown.map(c => (
                      <div key={c.id} className="br-tdc-comment">
                        <div className="br-tdc-comment-avatar">{(c.author?.name || "?")[0].toUpperCase()}</div>
                        <div className="br-tdc-comment-body">
                          <div className="br-tdc-comment-meta">
                            <span className="br-tdc-comment-author">{c.author?.name || "Unknown"}</span>
                            <span className="br-tdc-comment-time">{fmt(c.created_at)}</span>{c.is_query && <QueryBadge status={c.query_status} />}
                          </div>
                          <div className="br-tdc-comment-text">{c.body}</div>
                        </div>
                      </div>
                    ))}
                    {!expanded && hasMore && (
                      <button className="br-tdc-comments-viewmore" onClick={() => setExpandedComments(s => ({ ...s, [t.id]: true }))} type="button">View more comments</button>
                    )}
                    {expanded && hasMore && (
                      <button className="br-tdc-comments-showless" onClick={() => setExpandedComments(s => ({ ...s, [t.id]: false }))} type="button">Show less</button>
                    )}
                  </div>
                );
              })()}
              {editable && (
                <div className="br-task-comment-row" style={{ marginTop: 12 }}>
                  <input
                    className="br-task-comment-input"
                    type="text"
                    placeholder={askAsQuery ? "Ask a question the assignee must answer…" : "Add a comment for this task…"}
                    value={newTaskComment}
                    onChange={e => setNewTaskComment(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitTaskDetailComment(); }}
                  />
                  <AskAsQueryToggle checked={askAsQuery} onChange={setAskAsQuery} />
                  <button
                    className="br-btn br-btn-primary br-btn-sm"
                    onClick={submitTaskDetailComment}
                    disabled={postingComment || !newTaskComment.trim()}
                    type="button"
                  >
                    {postingComment ? 'Posting…' : 'Post'}
                  </button>
                </div>
              )}
            </div>
          </Modal>
        );
      })()}

      {openQuery && <QueryModal appId={appId} query={openQuery} onClose={() => setOpenQuery(null)} onChanged={silentRefresh} />}

      {modal?.kind === 'appreciation' && (
        <Modal open title="Give Appreciation 🙌" onClose={() => setModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {modalForm.err && <div className="feed-modal-error">{modalForm.err}</div>}
            <div className="filter-group" style={{ marginBottom: 0 }}>
              <label>Badge (optional)</label>
              <select value={modalForm.badge || ''}
                onChange={e => setModalForm(f => ({ ...f, badge: e.target.value }))}>
                <option value="">— No badge —</option>
                {Object.entries(BADGE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.emoji} {v.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group" style={{ marginBottom: 0 }}>
              <label>Your message *</label>
              <textarea rows={4} value={modalForm.message || ''}
                onChange={e => setModalForm(f => ({ ...f, message: e.target.value }))}
                placeholder="What did they do that deserves recognition?"
                style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="feed-btn-cancel" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary feed-btn-primary--warn" onClick={submitAppreciation}>🙌 Give Appreciation</button>
            </div>
          </div>
        </Modal>
      )}

      {modal?.kind === 'feedback' && (
        <Modal open title="Give Feedback 💬" onClose={() => setModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {modalForm.err && <div className="feed-modal-error">{modalForm.err}</div>}
            <div className="filter-group" style={{ marginBottom: 0 }}>
              <label>Your feedback *</label>
              <textarea rows={5} value={modalForm.feedback_text || ''}
                onChange={e => setModalForm(f => ({ ...f, feedback_text: e.target.value }))}
                placeholder="Be specific and constructive. Focus on behaviours and impact."
                style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={!!modalForm.is_anonymous}
                onChange={e => setModalForm(f => ({ ...f, is_anonymous: e.target.checked }))} />
              Submit anonymously (your name won't be shown to the recipient)
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="feed-btn-cancel" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={submitFeedback}>💬 Submit Feedback</button>
            </div>
          </div>
        </Modal>
      )}

      <CreateTaskModal
        open={!!createTaskFor}
        onClose={() => setCreateTaskFor(null)}
        members={members}
        roles={roles}
        areasByRole={areasByRole}
        defaultAssigneeUserId={createTaskFor}
        businessReviewId={Number(reviewId)}
        businessReviewFor={createTaskFor ? Number(createTaskFor) : undefined}
        onCreated={async () => { setCreateTaskFor(null); await silentRefresh(); }}
      />
    </div>
  );
}

// ── Monthly Review View ───────────────────────────────────────────────────────

export function MonthlyReviewView({ reviewId, appId, onBack }) {
  const api = useApi();
  const { me } = useWorkspace();

  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [drafts, setDrafts]         = useState({});
  const [remarks, setRemarks]       = useState('');
  const [saving, setSaving]         = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState(null);
  const [modal, setModal]           = useState(null); // { kind: 'appreciation'|'feedback', uid }
  const [modalForm, setModalForm]   = useState({});
  const [empIdx, setEmpIdx]         = useState(0);
  const [sectionModal, setSectionModal] = useState(null); // { uid, key } | null
  const [taskModal, setTaskModal] = useState(null);
  const [taskDetailModal, setTaskDetailModal] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [newTaskComment, setNewTaskComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [createTaskFor, setCreateTaskFor] = useState(null); // uid | null
  const [askAsQuery, setAskAsQuery]       = useState(false);
  const [queriesOpenFor, setQueriesOpenFor] = useState({}); // { [uid]: boolean }
  const [openQuery, setOpenQuery]         = useState(null);
  const [members, setMembers]         = useState([]);
  const [roles, setRoles]             = useState([]);
  const [areasByRole, setAreasByRole] = useState(new Map());

  useEffect(() => {
    if (!appId) return;
    Promise.all([
      api.listAppMembers(appId).catch(() => ({ items: [] })),
      api.roles.list(appId).catch(() => ({ items: [] })),
    ]).then(async ([m, r]) => {
      const memberRows = m.items || [];
      const roleRows   = r.items || [];
      setMembers(memberRows);
      setRoles(roleRows);
      const perRole = await Promise.all(
        roleRows.map(role =>
          api.listAreas(appId, role.id).then(res => ({ role, items: res.items || [] })).catch(() => ({ role, items: [] }))
        )
      );
      const map = new Map();
      for (const { role, items } of perRole) map.set(role.id, items);
      setAreasByRole(map);
    }).catch(() => {});
  }, [api, appId]);

  const applyData = useCallback((d) => {
    setData(d);
    const initDrafts = {};
    for (const [uid, rd] of Object.entries(d.reporteeData || {})) {
      const a = rd.assessment;
      if (a) {
        initDrafts[uid] = {
          overall_rating:    a.overall_rating    || '',
          score:             a.score != null ? Number(a.score) : null,
          task_notes:        a.task_notes        || '',
          project_notes:     a.project_notes     || '',
          goal_notes:        a.goal_notes        || '',
          recognition_notes: a.recognition_notes || '',
          development_notes: a.development_notes || '',
          comment:           a.comment           || '',
        };
      }
    }
    setDrafts(initDrafts);
    setRemarks(d.review?.meeting_notes || '');
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.getBusinessReview(appId, reviewId)
      .then(applyData)
      .catch(() => setError('Failed to load review'))
      .finally(() => setLoading(false));
  }, [appId, reviewId, applyData]);

  // Silent refresh — no loading spinner, used after save/submit
  const silentRefresh = useCallback(() => {
    api.getBusinessReview(appId, reviewId)
      .then(applyData)
      .catch(() => {});
  }, [appId, reviewId, applyData]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setNewTaskComment(''); }, [taskDetailModal?.id]);

  async function submitTaskDetailComment() {
    const body = newTaskComment.trim();
    if (!body || !taskDetailModal || postingComment) return;
    setPostingComment(true);
    try {
      const saved = await api.addComment(appId, taskDetailModal.id, askAsQuery ? { body, is_query: true, business_review_id: Number(reviewId) } : { body });
      const authorName = [me?.firstName, me?.lastName].filter(Boolean).join(' ') || me?.email || 'You';
      const newComment = { id: saved.id, body: saved.body, created_at: saved.created_at, is_query: saved.is_query, query_status: saved.query_status, author: { name: authorName } };
      if (saved.is_query) { setAskAsQuery(false); silentRefresh(); }
      setTaskDetailModal(cur => cur ? { ...cur, comments: [...(cur.comments || []), newComment] } : cur);
      setNewTaskComment('');
    } catch (err) {
      console.error(err);
    } finally {
      setPostingComment(false);
    }
  }

  const setDraft = (uid, field, value) =>
    setDrafts(prev => ({ ...prev, [uid]: { ...(prev[uid] || {}), [field]: value } }));

  async function saveMember(uid) {
    setSaving(s => ({ ...s, [uid]: true }));
    try {
      await api.upsertBRAssessment(appId, reviewId, uid, drafts[uid] || {});
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(s => ({ ...s, [uid]: false }));
    }
  }

  async function handleSubmit() {
    if (!remarks.trim()) {
      setError('Remarks / comments are required to complete the review.');
      return;
    }
    if (remarks.length > 1000) {
      setError('Remarks must be under 1000 characters.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const uids = Object.keys(data.reporteeData || {});
      await Promise.all(uids.map(uid => api.upsertBRAssessment(appId, reviewId, uid, drafts[uid] || {})));
      await api.updateBusinessReview(appId, reviewId, { meeting_notes: remarks });
      await api.closeBusinessReview(appId, reviewId, {});
      setSaved(true);
      silentRefresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitAppreciation() {
    if (!modalForm.message?.trim()) {
      setModalForm(f => ({ ...f, err: 'Please write a message' }));
      return;
    }
    try {
      await api.giveAppreciation(appId, {
        to_user_id: parseInt(modal.uid),
        message: modalForm.message.trim(),
        badge: modalForm.badge || null,
      });
      setModal(null); setModalForm({});
    } catch (err) {
      setModalForm(f => ({ ...f, err: err.response?.data?.error || 'Failed to send' }));
    }
  }

  async function submitFeedback() {
    if (!modalForm.feedback_text?.trim()) {
      setModalForm(f => ({ ...f, err: 'Please write your feedback' }));
      return;
    }
    try {
      await api.giveFeedback(appId, {
        to_user_id: parseInt(modal.uid),
        feedback_text: modalForm.feedback_text.trim(),
        is_anonymous: modalForm.is_anonymous ? 1 : 0,
      });
      setModal(null); setModalForm({});
    } catch (err) {
      setModalForm(f => ({ ...f, err: err.response?.data?.error || 'Failed to send' }));
    }
  }

  if (loading) return <div className="br-page"><div className="br-loading">Loading&hellip;</div></div>;
  if (!data) return (
    <div className="br-page">
      <div className="br-loading" style={{ color: 'var(--neg,#ef4444)' }}>{error || 'Not found'}</div>
    </div>
  );

  const { review, manager, reporteeData } = data;
  const canEdit  = review.manager_user_id === me?.id;
  const closed   = review.status === 'closed';
  const editable = canEdit && !closed;
  const uids     = Object.keys(reporteeData || {});

  if (uids.length === 0) return (
    <div className="br-page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: 40, minHeight: 320 }}>
      <div style={{ background: 'rgba(240,169,59,0.12)', border: '1px solid rgba(240,169,59,0.35)', borderRadius: 12, padding: '28px 32px', maxWidth: 440, textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--warn)', marginBottom: 8 }}>No Team Members Found</div>
        <div style={{ fontSize: 14, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
          This review has no team members assigned. It may have been created before members were assigned to you.
          Please contact your administrator to assign team members.
        </div>
      </div>
      <button className="btn-back" onClick={onBack}>←</button>
    </div>
  );

  const periodLabel = review.period_start
    ? new Date(review.period_start + 'T00:00:00').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="br-page">
      <div className="br-workspace-header">
        <button className="btn-back" onClick={onBack}>←</button>
        <div className="br-workspace-meta">
          <div className="br-workspace-title-row">
            <span className="br-workspace-title">Monthly Review</span>
            <Badge color={statusColor(review.status)}>{review.status}</Badge>
          </div>
          <div className="br-workspace-subtitle">
            {periodLabel} &nbsp;&middot;&nbsp; Manager: {manager?.name}
          </div>
        </div>
        {editable && (
          <AddMemberButton appId={appId} reviewId={reviewId} currentUids={uids} onAdded={silentRefresh} />
        )}
      </div>

      {!canEdit && (
        <div className="br-info-banner">This review can only be filled in by the manager.</div>
      )}

      {uids.length === 0 ? (
        <div className="br-empty">No team members found under this manager.</div>
      ) : (
        <div className="br-rc-list">
          {uids.map(uid => {
            const rd  = reporteeData[uid] || {};
            const d   = drafts[uid] || {};
            const s   = rd.summary || {};
            const taskHrsH = Math.floor((s.task_hours_minutes || 0) / 60);
            const taskHrsM = (s.task_hours_minutes || 0) % 60;
            const taskHrsLabel = taskHrsH > 0 ? `${taskHrsH}h ${taskHrsM}m` : `${taskHrsM}m`;
            const projects = s.projects || [];
            const contracts = s.contract_tasks || [];
            const perfReviews = s.perf_reviews || [];
            const goalsMap = s.goals_map || {};
            const goalsTotal = s.goals_total || 0;
            const routinesDone = Array.isArray(s.routines_done) ? s.routines_done.length : (s.routines_done || 0);
            const routinesPending = Array.isArray(s.routines_pending) ? s.routines_pending.length : (s.routines_pending || 0);
            const apprReceived = Array.isArray(s.appr_received) ? s.appr_received.length : (s.appr_received || 0);
            const apprGiven = Array.isArray(s.appr_given) ? s.appr_given.length : (s.appr_given || 0);
            const fbReceived = Array.isArray(s.feedback_received) ? s.feedback_received.length : (s.feedback_received || 0);
            const fbGiven = Array.isArray(s.feedback_given) ? s.feedback_given.length : (s.feedback_given || 0);
            const nameInitials = (rd.user?.name || `U${uid}`).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            const AVATAR_COLORS_M = ['#6366f1','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ef4444'];
            const avatarColorM = AVATAR_COLORS_M[(rd.user?.name || '').charCodeAt(0) % AVATAR_COLORS_M.length] || '#6366f1';
            const mTotalTasks = (s.completed_tasks || 0) + (s.pending_tasks_count || 0) + (s.on_hold_tasks || 0) + (s.overdue_tasks || 0);
            const mOnTimePct = mTotalTasks > 0 ? Math.round(((s.on_time_tasks || 0) / mTotalTasks) * 100) : 0;
            const perfPending = s.performance_pending || {};
            const reviewCreatedTasks = s.review_created_tasks || [];
            const carriedForwardTasks = s.carried_forward_tasks || [];
            return (
          <div key={uid} className="br-mnew-card">

            {/* Profile Header + Stats inline */}
            <div className="br-mnew-header">
              <div className="br-mnew-avatar" style={{ background: avatarColorM }}>{nameInitials}</div>
              <div className="br-mnew-identity">
                <div className="br-mnew-name">{rd.user?.name || `User ${uid}`}</div>
                {rd.user?.role_title && <div className="br-mnew-role">{rd.user.role_title}</div>}
                {rd.user?.employee_id && <div className="br-mnew-empid">Employee ID: GLOS-{rd.user.employee_id}</div>}
              </div>
              <div className="br-mnew-header-stats">
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{taskHrsLabel}</span><span className="br-mnew-stat-lbl">Hours Logged</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{s.completed_tasks || 0}</span><span className="br-mnew-stat-lbl">Tasks Completed</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{s.pending_tasks_count || 0}</span><span className="br-mnew-stat-lbl">Pending Tasks</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{mOnTimePct}%</span><span className="br-mnew-stat-lbl">On-Time Delivery</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val">{perfReviews.length}</span><span className="br-mnew-stat-lbl">Reviews</span></div>
                <div className="br-mnew-stat"><span className="br-mnew-stat-val br-mnew-trend-up">+{routinesDone}</span><span className="br-mnew-stat-lbl">Routines Done</span></div>
              </div>
              <PendingQueriesChip queries={rd.pending_queries} open={!!queriesOpenFor[uid]}
                onToggle={() => setQueriesOpenFor(s => ({ ...s, [uid]: !s[uid] }))} />
              {editable && (
                <button className="br-btn br-btn-secondary br-btn-sm" style={{ marginTop: 5 }} onClick={() => setCreateTaskFor(uid)}>+ Create Task</button>
              )}
            </div>

            {queriesOpenFor[uid] && <PendingQueriesList queries={rd.pending_queries} onOpen={setOpenQuery} />}

            {/* Still-open tasks carried forward from any earlier review —
                keeps reappearing every review until actually done, not just
                once right after it was created, so a task that's still
                unstarted 3-4 reviews later doesn't quietly fall out of view. */}
            <ReviewTasksPanel tasks={carriedForwardTasks} onTaskClick={setTaskDetailModal}
              title="Carried Forward — Not Yet Done" showLatestComment />

            {/* Tasks created in this review */}
            <ReviewTasksPanel tasks={reviewCreatedTasks} onTaskClick={setTaskDetailModal} />

            {/* Two-column: Tasks Overview + Manager Assessment */}
            <div className="br-mnew-main-row">
              <div className="br-mnew-tasks-panel">
                <div className="br-mnew-panel-title">Tasks Overview</div>
                <table className="br-mnew-task-table">
                  <thead>
                    <tr><th>Status</th><th>Count</th><th>Percentage</th><th></th></tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Completed', count: s.completed_tasks || 0, color: '#22c55e', list: s.completed_task_list || [] },
                      { label: 'Pending',   count: s.pending_tasks_count || 0, color: '#f59e0b', list: s.pending_task_list || [] },
                      { label: 'Blocked',   count: s.on_hold_tasks || 0, color: '#6366f1', list: s.on_hold_task_list || [] },
                      { label: 'Overdue',   count: s.overdue_tasks || 0, color: '#ef4444', list: s.overdue_task_list || [] },
                    ].map(row => {
                      const pct = mTotalTasks > 0 ? Math.round((row.count / mTotalTasks) * 100) : 0;
                      return (
                        <tr key={row.label}>
                          <td><span className="br-mnew-dot" style={{ background: row.color }} />{row.label}</td>
                          <td>{row.count}</td>
                          <td>
                            <div className="br-mnew-bar-wrap">
                              <div className="br-mnew-bar-fill" style={{ width: `${pct}%`, background: row.color }} />
                            </div>
                            <span className="br-mnew-bar-pct">{pct}%</span>
                          </td>
                          <td>
                            {row.count > 0 && (
                              <button className="br-mnew-view-btn" onClick={() => setTaskModal({ title: row.label, color: row.color, tasks: row.list })}>View</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <ReviewHistorySection appId={appId} reviewId={review.id} review={review} userId={Number(uid)} userName={rd.user?.name || `User ${uid}`} />
              </div>
              <div className="br-mnew-coll-panel">
                {[
                  { key: 'projects', icon: '📁', label: 'Projects', count: projects.length, content: (
                    projects.length === 0 ? <p className="br-rc-empty-hint">No projects this period.</p> :
                    <div className="br-rc-item-list">{projects.map(p => (
                      <div key={p.id} className="br-rc-item">
                        <div className={`br-rc-item-dot br-rc-item-dot--${p.is_completed ? 'done' : 'active'}`} />
                        <span className="br-rc-item-name">{p.name}</span>
                        <span className="br-rc-item-meta">{p.start_date ? fmt(p.start_date) : '—'} → {p.end_date ? fmt(p.end_date) : '—'}</span>
                        <span className={`br-rc-item-badge br-rc-item-badge--${p.is_completed ? 'done' : 'active'}`}>{p.is_completed ? 'Completed' : 'Active'}</span>
                      </div>
                    ))}</div>
                  )},
                  { key: 'contracts', icon: '📄', label: 'Agreements', count: contracts.length, content: (
                    contracts.length === 0 ? <p className="br-rc-empty-hint">No agreements this period.</p> :
                    <div className="br-rc-item-list">{contracts.map((c, ci) => (
                      <div key={ci} className="br-rc-item">
                        <div className={`br-rc-item-dot br-rc-item-dot--${c.status === 'done' ? 'done' : 'active'}`} />
                        <span className="br-rc-item-name">{c.service_name || c.client_name || 'Agreement'}</span>
                        <span className="br-rc-item-meta">{c.start_date ? fmt(c.start_date) : '—'} → {c.end_date ? fmt(c.end_date) : '—'}</span>
                        <span className={`br-rc-item-badge br-rc-item-badge--${c.status === 'done' ? 'done' : 'active'}`}>{c.status || 'active'}</span>
                      </div>
                    ))}</div>
                  )},
                  { key: 'feedback', icon: '💬', label: 'Feedback & Appreciation', count: fbReceived + apprReceived, content: (
                    <div className="br-mnew-fb-grid">
                      <div className="br-mnew-fb-stat"><span>{apprReceived}</span><small>Appr. Received</small></div>
                      <div className="br-mnew-fb-stat"><span>{apprGiven}</span><small>Appr. Given</small></div>
                      <div className="br-mnew-fb-stat"><span>{fbReceived}</span><small>Feedback Received</small></div>
                      <div className="br-mnew-fb-stat"><span>{fbGiven}</span><small>Feedback Given</small></div>
                    </div>
                  )},
                  { key: 'routines', icon: '🔄', label: 'Role Routines', count: routinesDone + routinesPending, content: (
                    <div className="br-mnew-fb-grid">
                      <div className="br-mnew-fb-stat"><span>{routinesDone}</span><small>Completed</small></div>
                      <div className="br-mnew-fb-stat"><span>{routinesPending}</span><small>Pending</small></div>
                    </div>
                  )},
                  { key: 'performance', icon: '📈', label: 'Performance', count: (perfPending.review_cycles?.length || 0) + (perfPending.pending_goals_count || 0), content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div className="br-mnew-fb-grid">
                        <div className="br-mnew-fb-stat"><span>{perfPending.review_cycles?.length || 0}</span><small>Reviews Pending</small></div>
                        <div className="br-mnew-fb-stat"><span>{perfPending.pending_goals_count || 0}</span><small>Goals Pending</small></div>
                      </div>
                      {(perfPending.review_cycles || []).length > 0 && (
                        <div className="br-rc-item-list">{perfPending.review_cycles.map(rc => (
                          <div key={rc.id} className="br-rc-item">
                            <div className="br-rc-item-dot br-rc-item-dot--active" />
                            <span className="br-rc-item-name">{rc.cycle_name}</span>
                            <span className={`br-rc-item-badge br-rc-item-badge--active`}>{REVIEW_STATUS_LABELS[rc.status] || rc.status}</span>
                          </div>
                        ))}</div>
                      )}
                    </div>
                  )},
                ].map(sec => (
                  <div key={sec.key} className="br-mnew-coll">
                    <button className="br-mnew-coll-header" onClick={() => setSectionModal({ uid, key: sec.key })}>
                      <span className="br-mnew-coll-icon">{sec.icon}</span>
                      <span className="br-mnew-coll-label">{sec.label}</span>
                      <span className="br-mnew-coll-count">{sec.count} {sec.label.split(' ')[0]}{sec.count !== 1 ? 's' : ''}</span>
                      <span className="br-mnew-coll-arrow">›</span>
                    </button>
                    <Modal
                      open={sectionModal?.uid === uid && sectionModal?.key === sec.key}
                      title={sec.label}
                      onClose={() => setSectionModal(null)}
                    >
                      {sec.content}
                    </Modal>
                  </div>
                ))}
              </div>
            </div>

            {/* Manager Assessment — full width below */}
            <div className="br-mnew-assess-full">
              <div className="br-mnew-assess-row">
                <div style={{ flex: 1 }}>
                  <div className="br-mnew-comment-label">Manager Comments</div>
                  {editable ? (
                    <textarea className="br-mnew-comment" placeholder="Write your feedback, observations and areas for growth..."
                      value={d.comment || ''} onChange={e => setDraft(uid, 'comment', e.target.value)} maxLength={1000} />
                  ) : (
                    d.comment ? <div className="br-monthly-notes-ro">{d.comment}</div> : <span className="br-rc-empty-hint">—</span>
                  )}
                </div>
              </div>
              {editable && (
                <div className="br-mnew-footer-row">
                  <button className="br-btn br-btn-ghost br-btn-sm"
                    onClick={() => { setModal({ kind: 'appreciation', uid }); setModalForm({ badge: 'star' }); }}>
                    🙌 Appreciation
                  </button>
                  <button className="br-btn br-btn-ghost br-btn-sm"
                    onClick={() => { setModal({ kind: 'feedback', uid }); setModalForm({}); }}>
                    💬 Feedback
                  </button>
                  <button className="br-btn br-btn-primary br-btn-sm" disabled={saving[uid]}
                    style={{ marginLeft: 'auto' }}
                    onClick={() => saveMember(uid)}>
                    {saving[uid] ? 'Saving…' : 'Save Review'}
                  </button>
                </div>
              )}
            </div>

          </div>
            );
          })}

          {editable && (
            <div className="br-monthly-card">
              <div className="br-monthly-section-title">Remarks <span className="br-required">*</span></div>
              <textarea className="br-monthly-notes" placeholder="Overall remarks for this month's review..."
                value={remarks} onChange={e => setRemarks(e.target.value.slice(0, 1000))} rows={3} maxLength={1000} />
              <span className={remarks.length > 1000 ? 'br-char-count br-char-count--over' : 'br-char-count'}>{remarks.length} / 1000</span>
              <div className="br-checkin-submit-row" style={{ marginTop: 12 }}>
                {error && <span className="br-checkin-error">{error}</span>}
                {saved && !submitting && (
                  <span className="br-checkin-saved">✓ All {uids.length} reviewed &amp; saved</span>
                )}
                <button className="br-btn br-btn-primary" disabled={submitting} onClick={handleSubmit}>
                  {submitting ? 'Saving...' : 'Complete Review'}
                </button>
              </div>
            </div>
          )}
          {!editable && review.meeting_notes && (
            <div className="br-monthly-card">
              <div className="br-monthly-section-title">Remarks</div>
              <div className="br-monthly-notes-ro">{review.meeting_notes}</div>
            </div>
          )}
        </div>
      )}

      {taskModal && (
        <Modal open size="lg" title={`${taskModal.title} Tasks`} onClose={() => setTaskModal(null)}>
          <div className="br-tmodal-wrap">
            {taskModal.tasks.length === 0 ? (
              <p className="br-rc-empty-hint">No task details available.</p>
            ) : (
              <table className="br-tmodal-table">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Priority</th>
                    <th>Due Date</th>
                    <th>Area</th>
                    {taskModal.title === 'Completed' && <th>Completed On</th>}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {taskModal.tasks.map(t => (
                    <tr key={t.id}
                      onClick={taskModal.title === 'Completed' ? () => { setTaskDetailModal(t); setTaskModal(null); } : undefined}
                      style={taskModal.title === 'Completed' ? { cursor: 'pointer' } : undefined}
                    >
                      <td className="br-tmodal-title">{t.title}</td>
                      <td><span className={`br-tmodal-priority br-tmodal-priority--${t.priority || 'normal'}`}>{t.priority || 'normal'}</span></td>
                      <td className="br-tmodal-date">{t.due_on ? new Date(t.due_on).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}</td>
                      <td className="br-tmodal-area">{t.area_name || '—'}</td>
                      {taskModal.title === 'Completed' && (
                        <td className="br-tmodal-date">{t.completed_at ? new Date(t.completed_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}</td>
                      )}
                      <td><span className="br-tmodal-status" style={{ background: taskModal.color + '22', color: taskModal.color }}>{TASK_STATUS_LABEL[t.status] || t.status?.replace(/_/g,' ')}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Modal>
      )}

      {taskDetailModal && (() => {
        const t = taskDetailModal;
        return (
          <Modal
            open
            size="lg"
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {t.title}
                {t.status && <span className={"br-task-status-badge " + (TASK_STATUS_CLASS[t.status] || '')}>{TASK_STATUS_LABEL[t.status] || t.status}</span>}
              </span>
            }
            onClose={() => setTaskDetailModal(null)}
          >
            <div className="br-task-modal-body">
              {t.area_name && <div className="br-task-modal-row"><span className="br-task-modal-label">Area</span><span className="br-task-area">{t.area_name}</span></div>}
              {t.due_on && <div className="br-task-modal-row"><span className="br-task-modal-label">Due</span><span className="br-tdc-due">{fmt(t.due_on)}</span></div>}
              {t.completed_at && <div className="br-task-modal-row"><span className="br-task-modal-label">Completed On</span><span className="br-tdc-due" style={{ color: '#22c55e' }}>{new Date(t.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>}
              {(t.estimated_minutes > 0 || t.actual_minutes > 0) && (
                <div className="br-task-modal-row">
                  <span className="br-task-modal-label">Time</span>
                  <div className="br-tdc-time-row" style={{ margin: 0 }}>
                    {t.estimated_minutes > 0 && <div className="br-tdc-time-chip"><span className="br-tdc-time-label">Estimation</span><span className="br-tdc-time-val">{fmtMins(t.estimated_minutes)}</span></div>}
                    {t.actual_minutes > 0 && <div className={"br-tdc-time-chip" + (t.actual_minutes > t.estimated_minutes && t.estimated_minutes > 0 ? " over" : "")}><span className="br-tdc-time-label">Time Spent</span><span className="br-tdc-time-val">{fmtMins(t.actual_minutes)}</span></div>}
                  </div>
                </div>
              )}
              {t.description && <div className="br-task-modal-desc"><span className="br-task-modal-label">Description</span><div className="br-tdc-description" style={{ marginTop: 4 }}>{t.description}</div></div>}
              {t.comments && t.comments.length > 0 && (() => {
                const LIMIT = 5;
                const expanded = expandedComments[t.id];
                const shown = expanded ? t.comments : t.comments.slice(0, LIMIT);
                const hasMore = t.comments.length > LIMIT;
                return (
                  <div className="br-tdc-comments" style={{ marginTop: 12 }}>
                    <div className="br-tdc-comments-label">Comments ({t.comments.length})</div>
                    {shown.map(c => (
                      <div key={c.id} className="br-tdc-comment">
                        <div className="br-tdc-comment-avatar">{(c.author?.name || "?")[0].toUpperCase()}</div>
                        <div className="br-tdc-comment-body">
                          <div className="br-tdc-comment-meta">
                            <span className="br-tdc-comment-author">{c.author?.name || "Unknown"}</span>
                            <span className="br-tdc-comment-time">{fmt(c.created_at)}</span>{c.is_query && <QueryBadge status={c.query_status} />}
                          </div>
                          <div className="br-tdc-comment-text">{c.body}</div>
                        </div>
                      </div>
                    ))}
                    {!expanded && hasMore && (
                      <button className="br-tdc-comments-viewmore" onClick={() => setExpandedComments(s => ({ ...s, [t.id]: true }))} type="button">View more comments</button>
                    )}
                    {expanded && hasMore && (
                      <button className="br-tdc-comments-showless" onClick={() => setExpandedComments(s => ({ ...s, [t.id]: false }))} type="button">Show less</button>
                    )}
                  </div>
                );
              })()}
              {editable && (
                <div className="br-task-comment-row" style={{ marginTop: 12 }}>
                  <input
                    className="br-task-comment-input"
                    type="text"
                    placeholder={askAsQuery ? "Ask a question the assignee must answer…" : "Add a comment for this task…"}
                    value={newTaskComment}
                    onChange={e => setNewTaskComment(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitTaskDetailComment(); }}
                  />
                  <AskAsQueryToggle checked={askAsQuery} onChange={setAskAsQuery} />
                  <button
                    className="br-btn br-btn-primary br-btn-sm"
                    onClick={submitTaskDetailComment}
                    disabled={postingComment || !newTaskComment.trim()}
                    type="button"
                  >
                    {postingComment ? 'Posting…' : 'Post'}
                  </button>
                </div>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* Appreciation modal */}
      {openQuery && <QueryModal appId={appId} query={openQuery} onClose={() => setOpenQuery(null)} onChanged={silentRefresh} />}

      {modal?.kind === 'appreciation' && (
        <Modal open title="Give Appreciation 🙌" onClose={() => setModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {modalForm.err && <div className="feed-modal-error">{modalForm.err}</div>}
            <div className="filter-group" style={{ marginBottom: 0 }}>
              <label>Badge (optional)</label>
              <select value={modalForm.badge || ''}
                onChange={e => setModalForm(f => ({ ...f, badge: e.target.value }))}>
                <option value="">— No badge —</option>
                {Object.entries(BADGE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.emoji} {v.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group" style={{ marginBottom: 0 }}>
              <label>Your message *</label>
              <textarea
                rows={4}
                value={modalForm.message || ''}
                onChange={e => setModalForm(f => ({ ...f, message: e.target.value }))}
                placeholder="What did they do that deserves recognition?"
                style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="feed-btn-cancel" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary feed-btn-primary--warn" onClick={submitAppreciation}>
                🙌 Give Appreciation
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Feedback modal */}
      {modal?.kind === 'feedback' && (
        <Modal open title="Give Feedback 💬" onClose={() => setModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {modalForm.err && <div className="feed-modal-error">{modalForm.err}</div>}
            <div className="filter-group" style={{ marginBottom: 0 }}>
              <label>Your feedback *</label>
              <textarea
                rows={5}
                value={modalForm.feedback_text || ''}
                onChange={e => setModalForm(f => ({ ...f, feedback_text: e.target.value }))}
                placeholder="Be specific and constructive. Focus on behaviours and impact."
                style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={!!modalForm.is_anonymous}
                onChange={e => setModalForm(f => ({ ...f, is_anonymous: e.target.checked }))} />
              Submit anonymously (your name won't be shown to the recipient)
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="feed-btn-cancel" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={submitFeedback}>
                💬 Submit Feedback
              </button>
            </div>
          </div>
        </Modal>
      )}

      <CreateTaskModal
        open={!!createTaskFor}
        onClose={() => setCreateTaskFor(null)}
        members={members}
        roles={roles}
        areasByRole={areasByRole}
        defaultAssigneeUserId={createTaskFor}
        businessReviewId={Number(reviewId)}
        businessReviewFor={createTaskFor ? Number(createTaskFor) : undefined}
        onCreated={async () => { setCreateTaskFor(null); await silentRefresh(); }}
      />
    </div>
  );
}

// ── Root Hub ──────────────────────────────────────────────────────────────────

export default function BusinessReviewHub() {
  const { appId } = useParams();
  const { myRole } = useWorkspace();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [openReview, setOpenReview] = useState(() => {
    const s = location.state;
    return s?.openId ? { id: s.openId, type: s.openType || 'daily' } : null;
  });
  const [monthFilter, setMonthFilter] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  });

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'reports',   label: 'Reports' },
  ];

  const handleOpenReview = (id, type) => setOpenReview({ id, type });
  const handleBack = () => setOpenReview(null);

  const [mfY, mfM] = monthFilter.split('-').map(Number);
  const monthLabel = new Date(mfY, mfM - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const now = new Date();
  const isCurrentMonth = mfY === now.getFullYear() && mfM === now.getMonth() + 1;
  function prevMonth() {
    const d = new Date(mfY, mfM - 2, 1);
    setMonthFilter(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  }
  function nextMonth() {
    if (isCurrentMonth) return;
    const d = new Date(mfY, mfM, 1);
    setMonthFilter(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  }

  if (openReview) {
    if (openReview.type === 'daily') {
      return <DailyCheckinView reviewId={openReview.id} appId={appId} onBack={handleBack} />;
    }
    if (openReview.type === 'weekly') {
      return <WeeklyCheckinView reviewId={openReview.id} appId={appId} onBack={handleBack} />;
    }
    if (openReview.type === 'monthly') {
      return <MonthlyReviewView reviewId={openReview.id} appId={appId} onBack={handleBack} />;
    }
    return (
      <div className="br-page">
        <ReviewWorkspace reviewId={openReview.id} appId={appId} onBack={handleBack} />
      </div>
    );
  }

  return (
    <div className="br-page">
      <div className="br-page-header">
        <h1 className="page-title">Business Reviews</h1>
        <div className="br-page-subtitle">Daily check-ins, weekly and monthly team reviews</div>
      </div>

      <div className="rh-tabs">
        <div className="rh-tabs-left">
          {tabs.map(t => (
            <button key={t.id}
              className={`rh-tab${activeTab === t.id ? ' active' : ''}`}
              onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="rh-tabs-month-nav">
          <button className="rh-month-nav-btn" onClick={prevMonth} title="Previous month">&#x2039;</button>
          <span className="rh-month-nav-label">{monthLabel}</span>
          <button className="rh-month-nav-btn" onClick={nextMonth} title="Next month" disabled={isCurrentMonth} style={isCurrentMonth ? { opacity: 0.35, cursor: 'default' } : {}}>&#x203a;</button>
        </div>
      </div>

      <div className="rh-tab-content">
        {activeTab === 'dashboard' && (
          <DashboardTab
            appId={appId}
            onOpenReview={handleOpenReview}
            canCreate={true}
            monthFilter={monthFilter}
          />
        )}
        {activeTab === 'reports' && <ReportsTab appId={appId} onOpenReview={handleOpenReview} monthFilter={monthFilter} />}
      </div>
    </div>
  );
}
